(() => {
  const initBerytBilan = (bilan) => {
    if (bilan.dataset.bilanInitialized === "true") {
      return;
    }

    bilan.dataset.bilanInitialized = "true";

    const state = {
      answers: {},

      lead: {
        first_name: "",
        email: "",
        bilan_data_consent: false,
        bilan_data_consent_version: null,
        bilan_data_consented_at: null,
        marketing_consent: false,
        marketing_consent_version: null
      }
    };

    const BILAN_DATA_CONSENT_VERSION =
      "2026-08-17-v1";

    const MARKETING_CONSENT_VERSION =
      "2026-08-17-v1";

    const BILAN_DRAFT_KEY =
      "beryt:bilan:draft:v1";

    const BILAN_DRAFT_VERSION =
      "2026-08-18-v1";

    const BILAN_DRAFT_TTL_MS =
      72 *
      60 *
      60 *
      1000;

    const BILAN_RESULT_SESSION_KEY =
      "beryt:bilan:result-session:v1";

    const BILAN_RESULT_SESSION_VERSION =
      "2026-08-18-v1";


    /* =========================
       ANALYTICS BERYT
    ========================== */

    const BILAN_ANALYTICS_VERSION =
      "2026-08-18-v1";

    const BILAN_ANALYTICS_ALLOWED_KEYS =
      new Set([
        "entry_mode",
        "question_id",
        "progress_pct",
        "generation_mode",
        "view_mode",
        "load_mode",
        "recovery_type",
        "restart_from",
        "error_stage",
        "online"
      ]);

    const trackBilanEvent = (
      eventName,
      eventData = {}
    ) => {
      const normalizedEventName =
        String(
          eventName ?? ""
        )
          .trim()
          .toLowerCase();

      if (!normalizedEventName) {
        return;
      }

      const safeEventData = {
        tracking_version:
          BILAN_ANALYTICS_VERSION
      };

      Object.entries(
        eventData
      ).forEach(
        ([
          key,
          value
        ]) => {
          if (
            !BILAN_ANALYTICS_ALLOWED_KEYS.has(
              key
            )
          ) {
            return;
          }

          if (
            typeof value !==
              "string" &&
            typeof value !==
              "number" &&
            typeof value !==
              "boolean"
          ) {
            return;
          }

          safeEventData[key] =
            value;
        }
      );

      const fullEventName =
        `beryt_bilan_${normalizedEventName}`;

      const isDebugMode =
        window
          .__BERYT_BILAN_TRACKING_DEBUG__ ===
        true;

      if (isDebugMode) {
        console.info(
          "BERYT Analytics — publish",
          {
            event:
              fullEventName,

            data:
              safeEventData
          }
        );
      }

      const analytics =
        window.Shopify
          ?.analytics;

      if (
        !analytics ||
        typeof analytics.publish !==
          "function"
      ) {
        if (isDebugMode) {
          console.warn(
            "BERYT Analytics — Shopify.analytics.publish indisponible",
            fullEventName
          );
        }

        return;
      }

      try {
        const publishResult =
          analytics.publish(
            fullEventName,
            safeEventData
          );

        if (
          publishResult &&
          typeof publishResult.catch ===
            "function"
        ) {
          publishResult.catch(
            (error) => {
              if (
                isDebugMode
              ) {
                console.warn(
                  "BERYT Analytics — événement non publié",
                  fullEventName,
                  error
                );
              }
            }
          );
        }
      } catch (error) {
        if (isDebugMode) {
          console.warn(
            "BERYT Analytics — erreur de publication",
            fullEventName,
            error
          );
        }
      }
    };


    const clearBilanResultSession =
      () => {
        try {
          window.sessionStorage.removeItem(
            BILAN_RESULT_SESSION_KEY
          );
        } catch {
          // Le navigateur peut bloquer
          // le stockage de session.
        }
      };

    const readBilanResultSession =
      () => {
        try {
          const raw =
            window.sessionStorage.getItem(
              BILAN_RESULT_SESSION_KEY
            );

          if (!raw) {
            return null;
          }

          const session =
            JSON.parse(
              raw
            );

          const isValid =
            session &&
            session.version ===
              BILAN_RESULT_SESSION_VERSION &&
            typeof session.result_id ===
              "string" &&
            session.result_id.length >
              0 &&
            typeof session.access_token ===
              "string" &&
            session.access_token.length >
              0;

          if (!isValid) {
            clearBilanResultSession();
            return null;
          }

          return session;
        } catch {
          clearBilanResultSession();
          return null;
        }
      };

    const saveBilanResultSession = (
      resultId,
      accessToken,
      firstName
    ) => {
      if (
        !resultId ||
        !accessToken
      ) {
        return;
      }

      try {
        window.sessionStorage.setItem(
          BILAN_RESULT_SESSION_KEY,
          JSON.stringify({
            version:
              BILAN_RESULT_SESSION_VERSION,

            saved_at:
              Date.now(),

            result_id:
              resultId,

            access_token:
              accessToken,

            first_name:
              typeof firstName ===
                "string"
                ? firstName
                : ""
          })
        );
      } catch {
        // Le résultat reste utilisable
        // dans l'onglet courant même si
        // sessionStorage est indisponible.
      }
    };

    let currentQuestionName =
      null;

    let currentProgressValue =
      0;

    let draftPhase =
      "intro";

    let isRestoringDraft =
      false;

    const screens = Array.from(
      bilan.querySelectorAll("[data-bilan-screen]")
    );

    const questions = Array.from(
      bilan.querySelectorAll("[data-bilan-question]")
    );


    /* =========================
       ÉCRAN DE REPRISE
    ========================== */

    const recoveryScreen =
      document.createElement(
        "div"
      );

    recoveryScreen.className =
      "beryt-bilan__screen beryt-bilan__onboarding-screen";

    recoveryScreen.dataset
      .bilanScreen =
      "recovery";

    recoveryScreen.hidden =
      true;

    recoveryScreen.innerHTML = `
      <div class="beryt-bilan__gate">

        <div class="beryt-bilan__gate-head">

          <p class="beryt-bilan__eyebrow">
            [ BILAN EN COURS ]
          </p>

          <p
            class="beryt-bilan__question-count"
            data-bilan-recovery-progress
          >
          </p>

        </div>

        <div class="beryt-bilan__gate-content">

          <p class="beryt-bilan__gate-index">
            REPRISE
          </p>

          <h2 class="beryt-bilan__gate-title">
            Nous avons retrouvé un Bilan en cours.
          </h2>

          <p class="beryt-bilan__gate-help">
            Vos dernières réponses sont encore disponibles sur cet appareil.
            Vous pouvez reprendre là où vous vous étiez arrêté
            ou repartir de zéro.
          </p>

          <p
            class="beryt-bilan__privacy-note"
            data-bilan-recovery-meta
          >
          </p>

        </div>

        <div class="beryt-bilan__gate-footer">

          <button
            type="button"
            class="beryt-bilan__back"
            data-bilan-recovery-restart
          >
            Recommencer
          </button>

          <button
            type="button"
            class="beryt-bilan__next"
            data-bilan-recovery-resume
          >
            Reprendre mon Bilan
            <span aria-hidden="true">→</span>
          </button>

        </div>

      </div>
    `;

    const screenHost =
      screens[0]?.parentElement ??
      bilan;

    screenHost.appendChild(
      recoveryScreen
    );

    screens.push(
      recoveryScreen
    );

    const recoveryProgress =
      recoveryScreen.querySelector(
        "[data-bilan-recovery-progress]"
      );

    const recoveryMeta =
      recoveryScreen.querySelector(
        "[data-bilan-recovery-meta]"
      );

    const recoveryResumeButton =
      recoveryScreen.querySelector(
        "[data-bilan-recovery-resume]"
      );

    const recoveryRestartButton =
      recoveryScreen.querySelector(
        "[data-bilan-recovery-restart]"
      );


    /* =========================
       RÉSULTAT DE SESSION
    ========================== */

    const resultRecoveryScreen =
      document.createElement(
        "div"
      );

    resultRecoveryScreen.className =
      "beryt-bilan__screen beryt-bilan__onboarding-screen beryt-bilan__result-recovery-screen";

    resultRecoveryScreen.dataset
      .bilanScreen =
      "result-recovery";

    resultRecoveryScreen.hidden =
      true;

          resultRecoveryScreen.innerHTML = `
      <div class="beryt-bilan__result-recovery-card">

        <div class="beryt-bilan__result-recovery-kicker">

          <span
            class="beryt-bilan__result-recovery-marker"
            aria-hidden="true"
          ></span>

          <span>
            BILAN RETROUVÉ
          </span>

        </div>

        <h2 class="beryt-bilan__result-recovery-title">
          Votre Bilan est toujours disponible.
        </h2>

        <p class="beryt-bilan__result-recovery-copy">
          Nous avons retrouvé le résultat généré pendant cette session.
          Vous pouvez le consulter directement sans recommencer
          le questionnaire.
        </p>

        <p
          class="beryt-bilan__result-recovery-error"
          data-bilan-result-recovery-error
          role="alert"
          aria-live="assertive"
          hidden
        ></p>

        <div class="beryt-bilan__result-recovery-actions">

          <button
            type="button"
            class="beryt-bilan__next"
            data-bilan-result-recovery-resume
          >
            Afficher mon Bilan
            <span aria-hidden="true">→</span>
          </button>

          <button
            type="button"
            class="beryt-bilan__back"
            data-bilan-result-recovery-restart
          >
            Recommencer
          </button>

        </div>

      </div>
    `;

    screenHost.appendChild(
      resultRecoveryScreen
    );

    screens.push(
      resultRecoveryScreen
    );

    const resultRecoveryResumeButton =
      resultRecoveryScreen.querySelector(
        "[data-bilan-result-recovery-resume]"
      );

    const resultRecoveryRestartButton =
      resultRecoveryScreen.querySelector(
        "[data-bilan-result-recovery-restart]"
      );

    const resultRecoveryError =
      resultRecoveryScreen.querySelector(
        "[data-bilan-result-recovery-error]"
      );

    const startButton = bilan.querySelector(
      "[data-bilan-start]"
    );
    const firstNameInput =
      bilan.querySelector(
        "[data-bilan-first-name]"
      );

    const firstNameNextButton =
      bilan.querySelector(
        "[data-bilan-first-name-next]"
      );

    const identityBackButton =
      bilan.querySelector(
        "[data-bilan-identity-back]"
      );

    const firstNameDisplays =
      Array.from(
        bilan.querySelectorAll(
          "[data-bilan-first-name-display]"
        )
      );

    const dataConsentInput =
      bilan.querySelector(
        "[data-bilan-data-consent]"
      );

    const consentNextButton =
      bilan.querySelector(
        "[data-bilan-consent-next]"
      );

    const consentBackButton =
      bilan.querySelector(
        "[data-bilan-consent-back]"
      );

    const emailInput =
      bilan.querySelector(
        "[data-bilan-email]"
      );

    const marketingConsentInput =
      bilan.querySelector(
        "[data-bilan-marketing-consent]"
      );

    const emailSubmitButton =
      bilan.querySelector(
        "[data-bilan-email-submit]"
      );

    const emailBackButton =
      bilan.querySelector(
        "[data-bilan-email-back]"
      );

    const submitError =
      bilan.querySelector(
        "[data-bilan-submit-error]"
      );

    const submitErrorText =
      bilan.querySelector(
        "[data-bilan-submit-error-text]"
      );

    const generationSteps =
      Array.from(
        bilan.querySelectorAll(
          "[data-bilan-generation-step]"
        )
      );

    const generationCurrent =
      bilan.querySelector(
        "[data-bilan-generation-current]"
      );

    const generationLine =
      bilan.querySelector(
        "[data-bilan-generation-line]"
      );

    const generationNextButton =
      bilan.querySelector(
        "[data-bilan-generation-next]"
      );

    const generationStatus =
      bilan.querySelector(
        "[data-bilan-generation-status]"
      );

    const generationTime =
      bilan.querySelector(
        "[data-bilan-generation-time]"
      );

    const progress = bilan.querySelector(
      "[data-bilan-progress]"
    );

    const methodInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="methods_used"]'
      )
    );

    const zoneInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="zones"]'
      )
    );

    const primaryZoneInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="zone_primary"]'
      )
    );

    const methodForZoneInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="method_for_primary_zone"]'
      )
    );

    const frequencyInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="frequency"]'
      )
    );

    const regrowthInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="regrowth_timing"]'
      )
    );

    const densityInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="density_declared"]'
      )
    );

    const thicknessInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="thickness_declared"]'
      )
    );

    const postEpilationSignInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="post_epilation_signs"]'
      )
    );

    const reactionDurationInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="reaction_duration"]'
      )
    );

    const ingrownFrequencyInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="ingrown_frequency"]'
      )
    );

    const ingrownManifestationInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="ingrown_manifestations"]'
      )
    );

    const comfortPerceptionInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="comfort_perception"]'
      )
    );

    const routineInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="routine"]'
      )
    );

    const exfoliationTimingInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="exfoliation_timing"]'
      )
    );

    const priorityInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="priority_ranking"]'
      )
    );

    const priorityRankingBlock =
      bilan.querySelector(
        "[data-bilan-priority-ranking]"
      );

    const priorityRankingList =
      bilan.querySelector(
        "[data-bilan-priority-ranking-list]"
      );

    const prioritySelectionStatus =
      bilan.querySelector(
        "[data-bilan-priority-selection-status]"
      );

    const safetyAnswerInputs = Array.from(
      bilan.querySelectorAll(
        'input[name="safety_answers"]'
      )
    );

    const primaryZoneBlock = bilan.querySelector(
      "[data-bilan-primary-zone]"
    );

    const primaryZoneOptions = Array.from(
      bilan.querySelectorAll(
        "[data-primary-zone-option]"
      )
    );

    const methodForZoneOptions = Array.from(
      bilan.querySelectorAll(
        "[data-method-for-zone-option]"
      )
    );

    const regrowthOptions = Array.from(
      bilan.querySelectorAll(
        "[data-regrowth-family]"
      )
    );

    const primaryZoneLabels = Array.from(
      bilan.querySelectorAll(
        "[data-bilan-primary-zone-label]"
      )
    );

    const regrowthTitle = bilan.querySelector(
      "[data-bilan-regrowth-title]"
    );

    const regrowthHelp = bilan.querySelector(
      "[data-bilan-regrowth-help]"
    );

    const methodsNextButton = bilan.querySelector(
      '[data-bilan-next="methods"]'
    );

    const zonesNextButton = bilan.querySelector(
      '[data-bilan-next="zones"]'
    );

    const zonesBackButton = bilan.querySelector(
      '[data-bilan-back="zones"]'
    );

    const methodForZoneNextButton =
      bilan.querySelector(
        '[data-bilan-next="method_for_primary_zone"]'
      );

    const methodForZoneBackButton =
      bilan.querySelector(
        '[data-bilan-back="method_for_primary_zone"]'
      );

    const frequencyNextButton =
      bilan.querySelector(
        '[data-bilan-next="frequency"]'
      );

    const frequencyBackButton =
      bilan.querySelector(
        '[data-bilan-back="frequency"]'
      );

    const regrowthNextButton =
      bilan.querySelector(
        '[data-bilan-next="regrowth_timing"]'
      );

    const regrowthBackButton =
      bilan.querySelector(
        '[data-bilan-back="regrowth_timing"]'
      );

    const densityNextButton =
      bilan.querySelector(
        '[data-bilan-next="density_declared"]'
      );

    const densityBackButton =
      bilan.querySelector(
        '[data-bilan-back="density_declared"]'
      );

    const thicknessNextButton =
      bilan.querySelector(
        '[data-bilan-next="thickness_declared"]'
      );

    const thicknessBackButton =
      bilan.querySelector(
        '[data-bilan-back="thickness_declared"]'
      );

    const postEpilationSignsNextButton =
      bilan.querySelector(
        '[data-bilan-next="post_epilation_signs"]'
      );

    const postEpilationSignsBackButton =
      bilan.querySelector(
        '[data-bilan-back="post_epilation_signs"]'
      );

    const reactionDurationNextButton =
      bilan.querySelector(
        '[data-bilan-next="reaction_duration"]'
      );

    const reactionDurationBackButton =
      bilan.querySelector(
        '[data-bilan-back="reaction_duration"]'
      );

    const ingrownFrequencyNextButton =
      bilan.querySelector(
        '[data-bilan-next="ingrown_frequency"]'
      );

    const ingrownFrequencyBackButton =
      bilan.querySelector(
        '[data-bilan-back="ingrown_frequency"]'
      );

    const ingrownManifestationsNextButton =
      bilan.querySelector(
        '[data-bilan-next="ingrown_manifestations"]'
      );

    const ingrownManifestationsBackButton =
      bilan.querySelector(
        '[data-bilan-back="ingrown_manifestations"]'
      );

    const comfortPerceptionNextButton =
      bilan.querySelector(
        '[data-bilan-next="comfort_perception"]'
      );

    const comfortPerceptionBackButton =
      bilan.querySelector(
        '[data-bilan-back="comfort_perception"]'
      );

    const routineNextButton =
      bilan.querySelector(
        '[data-bilan-next="routine"]'
      );

    const routineBackButton =
      bilan.querySelector(
        '[data-bilan-back="routine"]'
      );

    const exfoliationTimingNextButton =
      bilan.querySelector(
        '[data-bilan-next="exfoliation_timing"]'
      );

    const exfoliationTimingBackButton =
      bilan.querySelector(
        '[data-bilan-back="exfoliation_timing"]'
      );

    const priorityNextButton =
      bilan.querySelector(
        '[data-bilan-next="priority"]'
      );

    const priorityBackButton =
      bilan.querySelector(
        '[data-bilan-back="priority"]'
      );

    const safetyAnswersNextButton =
      bilan.querySelector(
        '[data-bilan-next="safety_answers"]'
      );

    const safetyAnswersBackButton =
      bilan.querySelector(
        '[data-bilan-back="safety_answers"]'
      );

    const zoneLabels = {
      legs: "Jambes",
      bikini: "Maillot",
      underarms: "Aisselles",
      face: "Visage",
      arms: "Bras",
      torso: "Torse / ventre",
      other: "Autre"
    };

    const methodFamilies = {
      razor: "surface",
      wax_sugar: "root",
      epilator: "root",
      depilatory_cream: "surface",
      tweezers_thread: "root",
      light_based: "light"
    };

    const getCheckedValues = (inputs) =>
      inputs
        .filter((input) => input.checked)
        .map((input) => input.value);


    /* =========================
       CONTEXTE DE ZONE + LECTURE RAPIDE
       Couche UI uniquement
    ========================== */

    const zoneContextQuestionNames = [
      "method_for_primary_zone",
      "frequency",
      "regrowth_timing",
      "density_declared",
      "thickness_declared",
      "post_epilation_signs",
      "reaction_duration",
      "ingrown_frequency",
      "ingrown_manifestations",
      "comfort_perception",
      "routine",
      "exfoliation_timing",
      "safety_answers"
    ];

    const zoneContextBlocks = [];

    zoneContextQuestionNames.forEach(
      (questionName) => {
        const question =
          questions.find(
            (item) =>
              item.dataset.bilanQuestion ===
              questionName
          );

        const fieldset =
          question?.querySelector(
            ".beryt-bilan__fieldset"
          );

        if (!question || !fieldset) {
          return;
        }

        const context =
          document.createElement("div");

        context.className =
          "beryt-bilan__question-context";

        context.dataset.bilanZoneContext =
          "";

        context.hidden = true;

        context.innerHTML = `
          <span>ZONE DE RÉFÉRENCE</span>
          <strong data-bilan-question-zone-label></strong>
        `;

        fieldset.before(context);

        zoneContextBlocks.push(context);
      }
    );

    const quickReadBlocks = {};

    [
      "frequency",
      "regrowth_timing",
      "post_epilation_signs",
      "reaction_duration"
    ].forEach((questionName) => {
      const question =
        questions.find(
          (item) =>
            item.dataset.bilanQuestion ===
            questionName
        );

      const fieldset =
        question?.querySelector(
          ".beryt-bilan__fieldset"
        );

      if (!question || !fieldset) {
        return;
      }

      const block =
        document.createElement("div");

      block.className =
        "beryt-bilan__quick-read";

      block.dataset.bilanQuickRead =
        questionName;

      block.setAttribute(
        "aria-live",
        "polite"
      );

      block.hidden = true;

      block.innerHTML = `
        <div class="beryt-bilan__quick-read-label">
          <span aria-hidden="true"></span>
          LECTURE RAPIDE
        </div>
        <strong data-bilan-quick-read-title></strong>
        <p data-bilan-quick-read-copy></p>
      `;

      fieldset.after(block);

      quickReadBlocks[questionName] =
        block;
    });

    const ensureQuickReadVisible = (
      block
    ) => {
      if (
        !block ||
        isRestoringDraft ||
        window.innerWidth > 749 ||
        !bilan.classList.contains(
          "is-experience-mode"
        )
      ) {
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const activeQuestion =
            block.closest(
              "[data-bilan-question]"
            );

          const footer =
            activeQuestion?.querySelector(
              ".beryt-bilan__question-footer"
            );

          if (!footer) {
            return;
          }

          const blockRect =
            block.getBoundingClientRect();

          const footerRect =
            footer.getBoundingClientRect();

          const safeBottom =
            footerRect.top - 18;

          if (
            blockRect.bottom <=
            safeBottom
          ) {
            return;
          }

          const scrollAmount =
            blockRect.bottom -
            safeBottom;

          bilan.scrollBy({
            top:
              scrollAmount +
              18,

            behavior:
              "smooth"
          });
        });
      });
    };


    const setQuickRead = (
      questionName,
      reading
    ) => {
      const block =
        quickReadBlocks[questionName];

      if (!block) {
        return;
      }

      const title =
        block.querySelector(
          "[data-bilan-quick-read-title]"
        );

      const copy =
        block.querySelector(
          "[data-bilan-quick-read-copy]"
        );

      if (!reading?.title) {
        block.hidden = true;

        if (title) {
          title.textContent = "";
        }

        if (copy) {
          copy.textContent = "";
        }

        return;
      }

      if (title) {
        title.textContent =
          reading.title;
      }

      if (copy) {
        copy.textContent =
          reading.copy ?? "";
      }

      block.hidden = false;

      ensureQuickReadVisible(
        block
      );
    };

    const frequencyQuickReads = {
      several_week: {
        title:
          "Vous vous épilez plusieurs fois par semaine.",
        copy:
          "Le délai auquel la repousse redevient visible aidera à replacer ce rythme dans son contexte."
      },
      weekly: {
        title:
          "Votre rythme est d’environ une séance par semaine.",
        copy:
          "Le Bilan va maintenant le mettre en relation avec le délai auquel votre repousse redevient visible."
      },
      every_2_3_weeks: {
        title:
          "Vos séances sont espacées de 2 à 3 semaines.",
        copy:
          "Le délai de repousse permettra de mieux comprendre ce rythme."
      },
      every_4_6_weeks: {
        title:
          "Vos séances sont espacées de 4 à 6 semaines.",
        copy:
          "La fréquence seule ne suffit pas à décrire votre repousse. La question suivante apporte ce contexte."
      },
      less_often: {
        title:
          "Vous vous épilez moins souvent.",
        copy:
          "Le Bilan va croiser ce rythme avec la manière dont votre repousse redevient visible."
      },
      variable: {
        title:
          "Votre rythme d’épilation varie beaucoup.",
        copy:
          "Cette variabilité sera conservée dans la lecture de votre profil avec vos autres réponses."
      }
    };

    const regrowthQuickReads = {
      surface: {
        surface_1_2d: {
          title:
            "La repousse redevient visible en 1 à 2 jours.",
          copy:
            "Ce délai est interprété dans le contexte d’une méthode qui agit à la surface du poil."
        },
        surface_3_4d: {
          title:
            "La repousse redevient visible en 3 à 4 jours.",
          copy:
            "Ce délai est interprété dans le contexte d’une méthode qui agit à la surface du poil."
        },
        surface_5_7d: {
          title:
            "La repousse redevient visible au cours de la première semaine.",
          copy:
            "Cette réponse sera mise en relation avec votre fréquence d’épilation."
        },
        surface_8plus: {
          title:
            "La repousse met plus d’une semaine à redevenir clairement visible.",
          copy:
            "Cette réponse sera mise en relation avec votre fréquence d’épilation."
        }
      },
      root: {
        root_week_1: {
          title:
            "La repousse redevient visible dès la première semaine.",
          copy:
            "Ce délai est interprété dans le contexte d’une méthode qui retire le poil à la racine."
        },
        root_week_2: {
          title:
            "La repousse redevient visible au cours de la deuxième semaine.",
          copy:
            "Ce délai est interprété dans le contexte d’une méthode qui retire le poil à la racine."
        },
        root_week_3: {
          title:
            "La repousse redevient visible au cours de la troisième semaine.",
          copy:
            "Cette réponse sera mise en relation avec votre fréquence d’épilation."
        },
        root_week_4plus: {
          title:
            "La repousse redevient visible à partir de la quatrième semaine.",
          copy:
            "Cette réponse sera mise en relation avec votre fréquence d’épilation."
        }
      },
      light: {
        light_high: {
          title:
            "La repousse reste importante aujourd’hui.",
          copy:
            "Le Bilan tient compte du fait que votre méthode de référence est l’IPL ou le laser."
        },
        light_medium_high: {
          title:
            "Vous observez une diminution, mais la repousse reste bien visible.",
          copy:
            "Cette situation actuelle sera utilisée pour contextualiser votre dimension Repousse."
        },
        light_low: {
          title:
            "Vous décrivez aujourd’hui une repousse faible.",
          copy:
            "Le Bilan tient compte du fait que votre méthode de référence est l’IPL ou le laser."
        },
        light_minimal: {
          title:
            "Vous décrivez aujourd’hui une repousse minime.",
          copy:
            "Le Bilan tient compte du fait que votre méthode de référence est l’IPL ou le laser."
        }
      }
    };

    const reactionDurationQuickReads = {
      under_1h: {
        title:
          "Les réactions s’estompent généralement en moins d’une heure.",
        copy:
          "Cette durée sera mise en relation avec les signes que vous avez indiqués."
      },
      few_hours: {
        title:
          "Les réactions persistent généralement quelques heures.",
        copy:
          "Cette durée sera mise en relation avec les signes que vous avez indiqués."
      },
      until_next_day: {
        title:
          "Les réactions peuvent persister jusqu’au lendemain.",
        copy:
          "Ce délai apporte un repère supplémentaire pour la lecture de votre réactivité."
      },
      beyond_next_day: {
        title:
          "Les réactions se prolongent au-delà du lendemain.",
        copy:
          "Ce délai apporte un repère supplémentaire pour la lecture de votre réactivité."
      },
      variable: {
        title:
          "La durée de vos réactions varie selon les séances.",
        copy:
          "Le Bilan conservera cette variabilité dans la lecture de votre profil."
      }
    };

    const syncFrequencyQuickRead = () => {
      setQuickRead(
        "frequency",
        frequencyQuickReads[
          state.answers.frequency
        ] ?? null
      );
    };

    const syncRegrowthQuickRead = () => {
      const value =
        state.answers.regrowth_timing;

      if (!value) {
        setQuickRead(
          "regrowth_timing",
          null
        );
        return;
      }

      if (value === "unknown") {
        setQuickRead(
          "regrowth_timing",
          {
            title:
              "Vous avez du mal à estimer précisément votre repousse.",
            copy:
              "Le Bilan s’appuiera sur vos autres réponses pour contextualiser cette dimension."
          }
        );
        return;
      }

      const family =
        getRegrowthFamily();

      setQuickRead(
        "regrowth_timing",
        regrowthQuickReads[family]?.[
          value
        ] ?? null
      );
    };

    const syncPostEpilationQuickRead = () => {
      const signs =
        state.answers.post_epilation_signs;

      if (
        !Array.isArray(signs) ||
        signs.length === 0
      ) {
        setQuickRead(
          "post_epilation_signs",
          null
        );
        return;
      }

      if (signs.includes("nothing")) {
        setQuickRead(
          "post_epilation_signs",
          {
            title:
              "Vous ne remarquez habituellement rien de particulier juste après l’épilation.",
            copy:
              "Le Bilan poursuit donc sans question supplémentaire sur la durée des réactions."
          }
        );
        return;
      }

      setQuickRead(
        "post_epilation_signs",
        {
          title:
            signs.length === 1
              ? "Vous remarquez un signe juste après l’épilation."
              : "Vous remarquez plusieurs signes juste après l’épilation.",
          copy:
            "La question suivante précisera combien de temps ils persistent généralement."
        }
      );
    };

    const syncReactionDurationQuickRead =
      () => {
        setQuickRead(
          "reaction_duration",
          reactionDurationQuickReads[
            state.answers.reaction_duration
          ] ?? null
        );
      };


    /* =========================
       BROUILLON LOCAL
    ========================== */

    const clearBilanDraft =
      () => {
        try {
          window.localStorage.removeItem(
            BILAN_DRAFT_KEY
          );
        } catch {
          // Le navigateur peut bloquer
          // le stockage local.
        }
      };

    const readBilanDraft =
      () => {
        try {
          const raw =
            window.localStorage.getItem(
              BILAN_DRAFT_KEY
            );

          if (!raw) {
            return null;
          }

          const draft =
            JSON.parse(
              raw
            );

          const savedAt =
            Number(
              draft?.saved_at
            );

          const isValid =
            draft &&
            draft.version ===
              BILAN_DRAFT_VERSION &&
            Number.isFinite(
              savedAt
            ) &&
            draft.answers &&
            typeof draft.answers ===
              "object";

          if (!isValid) {
            clearBilanDraft();
            return null;
          }

          const isExpired =
            Date.now() -
              savedAt >
            BILAN_DRAFT_TTL_MS;

          if (isExpired) {
            clearBilanDraft();
            return null;
          }

          return draft;
        } catch {
          clearBilanDraft();
          return null;
        }
      };

    const saveBilanDraft =
      () => {
        if (
          isRestoringDraft ||
          !state.lead
            .bilan_data_consent ||
          ![
            "questionnaire",
            "generation",
            "email"
          ].includes(
            draftPhase
          )
        ) {
          return;
        }

        try {
          const draft = {
            version:
              BILAN_DRAFT_VERSION,

            saved_at:
              Date.now(),

            phase:
              draftPhase,

            current_question:
              currentQuestionName,

            progress:
              currentProgressValue,

            first_name:
              state.lead.first_name,

            bilan_data_consent_version:
              state.lead
                .bilan_data_consent_version,

            bilan_data_consented_at:
              state.lead
                .bilan_data_consented_at,

            answers:
              JSON.parse(
                JSON.stringify(
                  state.answers
                )
              )
          };

          window.localStorage.setItem(
            BILAN_DRAFT_KEY,
            JSON.stringify(
              draft
            )
          );
        } catch {
          // Le Bilan continue de fonctionner
          // même si le stockage local échoue.
        }
      };

    const formatDraftAge = (
      savedAt
    ) => {
      const elapsedSeconds =
        Math.max(
          0,
          Math.floor(
            (
              Date.now() -
              Number(savedAt)
            ) /
              1000
          )
        );

      if (
        elapsedSeconds <
        60
      ) {
        return "il y a moins d’une minute";
      }

      const elapsedMinutes =
        Math.floor(
          elapsedSeconds /
            60
        );

      if (
        elapsedMinutes <
        60
      ) {
        return `il y a ${elapsedMinutes} ${
          elapsedMinutes === 1
            ? "minute"
            : "minutes"
        }`;
      }

      const elapsedHours =
        Math.floor(
          elapsedMinutes /
            60
        );

      if (
        elapsedHours <
        24
      ) {
        return `il y a ${elapsedHours} ${
          elapsedHours === 1
            ? "heure"
            : "heures"
        }`;
      }

      const elapsedDays =
        Math.floor(
          elapsedHours /
            24
        );

      return `il y a ${elapsedDays} ${
        elapsedDays === 1
          ? "jour"
          : "jours"
      }`;
    };


    const showScreen = (screenName) => {
      screens.forEach((screen) => {
        const isActive =
          screen.dataset.bilanScreen === screenName;

        screen.hidden = !isActive;

        screen.classList.toggle(
          "is-active",
          isActive
        );
      });
    };

    const setProgress = (value) => {
      const normalizedValue = Math.max(
        0,
        Math.min(100, value)
      );

      currentProgressValue =
        normalizedValue;

      if (!progress) {
        return;
      }

      progress.style.width =
        `${normalizedValue}%`;
    };

const scrollToBilanTop = () => {
  if (
    bilan.classList.contains(
      "is-experience-mode"
    )
  ) {
    bilan.scrollTop = 0;
    return;
  }

  const top =
    bilan.getBoundingClientRect().top +
    window.scrollY -
    24;

  window.scrollTo({
    top,
    behavior: "smooth"
  });
};

    const setExperienceMode = (
      isActive
    ) => {
      bilan.classList.toggle(
        "is-experience-mode",
        isActive
      );

      document.body.classList.toggle(
        "beryt-bilan-experience-lock",
        isActive
      );

      if (isActive) {
        bilan.scrollTop = 0;
      }
    };

const focusQuestion = (question) => {
  const heading = question?.querySelector(
    ".beryt-bilan__question-title"
  );

  if (!heading) {
    return;
  }

  heading.setAttribute(
    "tabindex",
    "-1"
  );

  heading.style.outline =
    "none";

  heading.style.boxShadow =
    "none";

  requestAnimationFrame(() => {
    heading.focus({
      preventScroll: true
    });
  });
};
    const showQuestion = (
      questionName,
      progressValue
    ) => {
      let activeQuestion = null;

      questions.forEach((question) => {
        const isActive =
          question.dataset.bilanQuestion ===
          questionName;

        question.hidden = !isActive;

        if (isActive) {
          activeQuestion = question;
        }
      });

      if (activeQuestion) {
        const progressLabel =
          activeQuestion.querySelector(
            ".beryt-bilan__question-count"
          );

        if (progressLabel) {
          progressLabel.textContent =
            `${Math.round(progressValue)} % COMPLÉTÉ`;
        }
      }

      currentQuestionName =
        questionName;

      draftPhase =
        "questionnaire";

      setProgress(
        progressValue
      );

      scrollToBilanTop();
      focusQuestion(activeQuestion);

      if (
        activeQuestion
      ) {
        trackBilanEvent(
          "question_viewed",
          {
            question_id:
              questionName,

            progress_pct:
              Math.round(
                progressValue
              )
          }
        );
      }

      saveBilanDraft();
    };
    let isSubmitting = false;
    let isDownloadingPdf = false;

    let completedResultId = null;
    let completedAccessToken = null;
    let completedResult = null;

    const pdfDownloadButton =
      bilan.querySelector(
        "[data-bilan-pdf-download]"
      );

    const pdfDownloadLabel =
      bilan.querySelector(
        "[data-bilan-pdf-label]"
      );

    const pdfDownloadStatus =
      bilan.querySelector(
        "[data-bilan-pdf-status]"
      );


    const getPdfAccessToken = () => {
      if (
        typeof completedAccessToken ===
          "string" &&
        completedAccessToken.length > 0
      ) {
        return completedAccessToken;
      }

      const session =
        readBilanResultSession();

      return (
        session?.access_token ??
        null
      );
    };


    const buildPdfFilename = () => {
      const session =
        readBilanResultSession();

      const firstName =
        String(
          state.lead.first_name ||
          session?.first_name ||
          ""
        ).trim();

      const safeFirstName =
        firstName
          .normalize("NFD")
          .replace(
            /[\u0300-\u036f]/g,
            ""
          )
          .replace(
            /[^a-zA-Z0-9_-]+/g,
            "-"
          )
          .replace(
            /^-+|-+$/g,
            ""
          )
          .slice(
            0,
            40
          );

      const now =
        new Date();

      const year =
        String(
          now.getFullYear()
        );

      const month =
        String(
          now.getMonth() + 1
        ).padStart(
          2,
          "0"
        );

      const day =
        String(
          now.getDate()
        ).padStart(
          2,
          "0"
        );

      const identityPart =
        safeFirstName
          ? `-${safeFirstName}`
          : "";

      return (
        `BERYT-Bilan${identityPart}-${year}-${month}-${day}.pdf`
      );
    };


    const setPdfStatus = (
      message,
      isError = false
    ) => {
      if (
        !pdfDownloadStatus
      ) {
        return;
      }

      pdfDownloadStatus.textContent =
        message;

      pdfDownloadStatus.hidden =
        !message;

      pdfDownloadStatus.classList.toggle(
        "is-error",
        isError
      );
    };


    const downloadBilanPdf =
      async () => {
        if (
          isDownloadingPdf
        ) {
          return;
        }

        const accessToken =
          getPdfAccessToken();

        if (!accessToken) {
          setPdfStatus(
            "Votre rapport n’est plus accessible dans cette session. Rechargez votre Bilan puis réessayez.",
            true
          );

          return;
        }

        isDownloadingPdf =
          true;

        setPdfStatus(
          ""
        );

        if (
          pdfDownloadButton
        ) {
          pdfDownloadButton.disabled =
            true;

          pdfDownloadButton.setAttribute(
            "aria-busy",
            "true"
          );
        }

        if (
          pdfDownloadLabel
        ) {
          pdfDownloadLabel.textContent =
            "Préparation du rapport…";
        }

        try {
          const response =
            await fetch(
              "/apps/beryt-bilan/pdf",
              {
                method: "POST",

                headers: {
                  "content-type":
                    "application/json",

                  accept:
                    "application/pdf, application/json"
                },

                credentials:
                  "same-origin",

                body:
                  JSON.stringify({
                    access_token:
                      accessToken
                  })
              }
            );

          const contentType =
            response.headers.get(
              "content-type"
            ) || "";

          if (
            !response.ok ||
            !contentType.includes(
              "application/pdf"
            )
          ) {
            let message =
              "Impossible de préparer votre rapport pour le moment.";

            try {
              const data =
                await response.json();

              message =
                data?.error?.message ??
                message;
            } catch {
              // Réponse non JSON :
              // on conserve le message générique.
            }

            throw new Error(
              message
            );
          }

          const blob =
            await response.blob();

          const blobUrl =
            URL.createObjectURL(
              blob
            );

          const link =
            document.createElement(
              "a"
            );

          link.href =
            blobUrl;

          link.download =
            buildPdfFilename();

          document.body.appendChild(
            link
          );

          link.click();
          link.remove();

          window.setTimeout(
            () => {
              URL.revokeObjectURL(
                blobUrl
              );
            },
            2000
          );

          if (
            pdfDownloadLabel
          ) {
            pdfDownloadLabel.textContent =
              "Rapport téléchargé";
          }

          setPdfStatus(
            "Votre rapport PDF a bien été généré."
          );

          window.setTimeout(
            () => {
              if (
                pdfDownloadLabel
              ) {
                pdfDownloadLabel.textContent =
                  "Télécharger mon rapport";
              }

              setPdfStatus(
                ""
              );
            },
            2200
          );
        } catch (error) {
          console.error(
            "BERYT Bilan — erreur PDF",
            error
          );

          if (
            pdfDownloadLabel
          ) {
            pdfDownloadLabel.textContent =
              "Télécharger mon rapport";
          }

          setPdfStatus(
            error instanceof Error
              ? error.message
              : "Impossible de préparer votre rapport pour le moment.",
            true
          );
        } finally {
          isDownloadingPdf =
            false;

          if (
            pdfDownloadButton
          ) {
            pdfDownloadButton.disabled =
              false;

            pdfDownloadButton.removeAttribute(
              "aria-busy"
            );
          }
        }
      };


    pdfDownloadButton
      ?.addEventListener(
        "click",
        downloadBilanPdf
      );


    const loadBilanResult = async (
      {
        allowUnknownResultId =
          false
      } = {}
    ) => {
      const expectedResultId =
        completedResultId;

      if (
        !completedAccessToken ||
        (
          !expectedResultId &&
          !allowUnknownResultId
        )
      ) {
        throw new Error(
          "Le bilan doit être créé avant de charger son résultat."
        );
      }

      const response = await fetch(
        "/apps/beryt-bilan/result",
        {
          method: "POST",
          headers: {
            "content-type":
              "application/json",
            accept:
              "application/json"
          },
          credentials:
            "same-origin",
          body:
            JSON.stringify({
              access_token:
                completedAccessToken
            })
        }
      );

      let data = null;

      try {
        data =
          await response.json();
      } catch {
        data = null;
      }

      if (
        !response.ok ||
        !data?.ok ||
        !data.result_id ||
        !data.result
      ) {
        const message =
          data?.error?.message ??
          `Impossible de charger le résultat (${response.status}).`;

        console.error(
          "BERYT Bilan — /result refusé",
          {
            status:
              response.status,
            error:
              data?.error ?? null
          }
        );

        throw new Error(
          message
        );
      }

      if (
        expectedResultId &&
        data.result_id !==
          expectedResultId
      ) {
        throw new Error(
          "Le résultat chargé ne correspond pas au bilan créé."
        );
      }

      completedResultId =
        data.result_id;

      completedResult =
        data.result;

      if (
        typeof data.first_name ===
          "string" &&
        data.first_name
          .trim()
          .length > 0
      ) {
        state.lead.first_name =
          data.first_name.trim();

        firstNameDisplays.forEach(
          (element) => {
            element.textContent =
              state.lead.first_name;
          }
        );
      }

      window.__BERYT_BILAN_RESULT__ =
        completedResult;

      if (
        window.__BERYT_BILAN_DEBUG__ ===
        true
      ) {
        console.log(
          "BERYT Bilan — /result OK",
          {
            result_id:
              data.result_id,

            same_result_id:
              expectedResultId
                ? data.result_id ===
                  expectedResultId
                : true,

            profile_code:
              completedResult?.profile
                ?.code ?? null,

            answers_exposed:
              Object.prototype.hasOwnProperty.call(
                completedResult,
                "answers"
              )
          }
        );
      }

      return completedResult;
    };

    const renderResultHero = (
      result
    ) => {
      const profile =
        result?.profile;

      const axes =
        result?.axes;

      const insights =
        Array.isArray(result?.insights)
          ? result.insights
          : [];

      const primaryInsight =
        [...insights]
          .sort(
            (a, b) =>
              Number(a.rank) -
              Number(b.rank)
          )[0];

      const safetyTriggered =
        result?.safety?.triggered ===
        true;

      const safetyIsPriorityCare =
        result?.safety?.status ===
        "priority_care";

      const safetyReasonsForWeb =
        Array.isArray(
          result?.safety?.reasons
        )
          ? result.safety.reasons.filter(
              (reason) =>
                reason &&
                reason !== "none"
            )
          : [];

      if (
        !profile?.code ||
        !profile?.label ||
        !axes ||
        !primaryInsight?.text
      ) {
        throw new Error(
          "Le résultat BERYT est incomplet et ne peut pas être affiché."
        );
      }

      const profileLabel =
        bilan.querySelector(
          "[data-bilan-result-profile-label]"
        );

      const profileCodes =
        Array.from(
          bilan.querySelectorAll(
            "[data-bilan-result-profile-code]"
          )
        );

      const profileTypeElement =
        bilan.querySelector(
          "[data-bilan-result-profile-type]"
        );

      const primaryInsightElement =
        bilan.querySelector(
          "[data-bilan-result-primary-insight]"
        );

      if (
        !profileLabel ||
        profileCodes.length === 0 ||
        !primaryInsightElement
      ) {
        throw new Error(
          "L’écran résultat BERYT est incomplet."
        );
      }

      const displayProfileCode =
        profile.code.replace(
          /_/g,
          " × "
        );

      const axisLabels = {
        regrowth:
          "Repousse",

        density_thickness:
          "Densité & épaisseur",

        reactivity:
          "Réactivité",

        ingrown:
          "Poils incarnés",

        comfort_need:
          "Besoin de confort"
      };

      const axisOrder = [
        "regrowth",
        "density_thickness",
        "reactivity",
        "ingrown",
        "comfort_need"
      ];

      const availableAxesForWeb =
        axisOrder
          .map(
            (key) => {
              const axis =
                axes[key];

              return {
                key,

                score:
                  typeof axis?.score ===
                    "number"
                    ? Math.round(
                        Math.max(
                          0,
                          Math.min(
                            100,
                            axis.score
                          )
                        )
                      )
                    : null
              };
            }
          )
          .filter(
            (item) =>
              item.score !==
              null
          )
          .sort(
            (a, b) =>
              b.score -
              a.score
          );

      const dimensionsCutoffForWeb =
        availableAxesForWeb.length >=
        2
          ? availableAxesForWeb[
              1
            ].score
          : availableAxesForWeb[
              0
            ]?.score ??
            null;

      const leadAxesForWeb =
        dimensionsCutoffForWeb ===
        null
          ? []
          : availableAxesForWeb.filter(
              (item) =>
                item.score >=
                dimensionsCutoffForWeb
            );

      const leadAxisKeys =
        new Set(
          leadAxesForWeb.map(
            (item) =>
              item.key
          )
        );


      const profileTypeText =
        profile.primary === "BAL"
          ? "LECTURE ÉQUILIBRÉE"
          : profile.secondary
            ? "PROFIL COMBINÉ"
            : "PROFIL PRINCIPAL";

      const safetyStatusLabel =
        safetyIsPriorityCare
          ? "PRIORITÉ SANTÉ"
          : "AVIS D’UN PROFESSIONNEL DE SANTÉ";


      const resultScreen =
        bilan.querySelector(
          '[data-bilan-screen="result"]'
        );

      const heroIndex =
        bilan.querySelector(
          "[data-bilan-result-hero-index]"
        );

      const personalCopy =
        bilan.querySelector(
          "[data-bilan-result-personal-copy]"
        );

      const metaValue =
        bilan.querySelector(
          "[data-bilan-result-meta-value]"
        );

      const primaryReading =
        bilan.querySelector(
          "[data-bilan-result-primary-reading]"
        );

      const primaryReadingLabel =
        bilan.querySelector(
          "[data-bilan-result-primary-reading-label]"
        );

      const safetyNotice =
        bilan.querySelector(
          "[data-bilan-result-safety-notice]"
        );

      const safetyStatus =
        bilan.querySelector(
          "[data-bilan-result-safety-status]"
        );

      const safetyCopy =
        bilan.querySelector(
          "[data-bilan-result-safety-copy]"
        );

      const summaryEyebrow =
        bilan.querySelector(
          "[data-bilan-result-summary-eyebrow]"
        );

      const summaryProfile =
        bilan.querySelector(
          "[data-bilan-result-summary-profile]"
        );

      const summaryType =
        bilan.querySelector(
          "[data-bilan-result-summary-type]"
        );

      const summaryProfileLabel =
        bilan.querySelector(
          "[data-bilan-result-summary-profile-label]"
        );

      const summaryListLabel =
        bilan.querySelector(
          "[data-bilan-result-summary-list-label]"
        );

      const summaryCount =
        bilan.querySelector(
          "[data-bilan-result-summary-count]"
        );

      const summaryList =
        bilan.querySelector(
          "[data-bilan-result-summary-list]"
        );

      const axesSummaryLabel =
        bilan.querySelector(
          "[data-bilan-result-axes-summary-label]"
        );

      const axesSummaryNames =
        bilan.querySelector(
          "[data-bilan-result-axes-summary-names]"
        );

      const axesSummaryNote =
        bilan.querySelector(
          "[data-bilan-result-axes-summary-note]"
        );


      resultScreen?.classList.toggle(
        "is-safety",
        safetyTriggered
      );

      resultScreen?.classList.toggle(
        "is-priority-care",
        safetyTriggered &&
          safetyIsPriorityCare
      );


      profileLabel.textContent =
        safetyTriggered
          ? "Ce qui compte maintenant."
          : profile.label;

      profileCodes.forEach(
        (element) => {
          element.textContent =
            displayProfileCode;
        }
      );


      if (heroIndex) {
        heroIndex.textContent =
          safetyTriggered
            ? "01 — CE QUI COMPTE MAINTENANT"
            : "01 — VOTRE PROFIL BERYT";
      }


      if (personalCopy) {
        personalCopy.textContent =
          safetyTriggered
            ? "votre Bilan appelle d’abord un repère de prudence."
            : "voici ce qui ressort de votre Bilan.";
      }


      if (profileTypeElement) {
        profileTypeElement.textContent =
          safetyTriggered
            ? safetyStatusLabel
            : profileTypeText;
      }


      if (metaValue) {
        metaValue.textContent =
          safetyTriggered
            ? "REPÈRES DE PRUDENCE"
            : displayProfileCode;
      }


      const profileSummaryFragments = {
        RG:
          "une repousse qui ressort nettement",

        RE:
          "une peau qui réagit nettement après l’épilation",

        IG:
          "une tendance marquée aux poils incarnés",

        CF:
          "un besoin de confort plus présent",

        DT:
          "des caractéristiques de poils particulièrement marquées"
      };


      if (
        profile.primary ===
        "BAL"
      ) {
        primaryInsightElement.textContent =
          "Votre Bilan fait ressortir plusieurs dimensions d’importance comparable, sans qu’une seule ne prenne nettement le dessus.";
      } else {
        const primarySummary =
          profileSummaryFragments[
            profile.primary
          ] ??
          profile.label;

        const secondarySummary =
          profile.secondary
            ? profileSummaryFragments[
                profile.secondary
              ]
            : null;

        primaryInsightElement.textContent =
          secondarySummary
            ? `Votre Bilan fait surtout ressortir ${primarySummary} et ${secondarySummary}.`
            : `Votre Bilan fait surtout ressortir ${primarySummary}.`;
      }


      if (primaryReading) {
        primaryReading.hidden =
          safetyTriggered;
      }


      if (primaryReadingLabel) {
        primaryReadingLabel.textContent =
          profile.primary === "BAL"
            ? "LECTURE DU PROFIL"
            : "CE QUI RESSORT EN PREMIER";
      }


      if (safetyNotice) {
        safetyNotice.hidden =
          !safetyTriggered;
      }


      if (
        safetyTriggered &&
        safetyStatus
      ) {
        safetyStatus.textContent =
          safetyStatusLabel;
      }


      if (
        safetyTriggered &&
        safetyCopy
      ) {
        safetyCopy.textContent =
          safetyReasonsForWeb.length ===
          1
            ? "Le signe que vous avez signalé passe avant toute recommandation cosmétique personnalisée. Vos réponses et les éléments descriptifs qui en découlent restent accessibles dans ce Bilan. En revanche, BERYT suspend volontairement les recommandations cosmétiques habituelles et vous présente en priorité les repères de prudence adaptés à votre réponse."
            : "Les signes que vous avez signalés passent avant toute recommandation cosmétique personnalisée. Vos réponses et les éléments descriptifs qui en découlent restent accessibles dans ce Bilan. En revanche, BERYT suspend volontairement les recommandations cosmétiques habituelles et vous présente en priorité les repères de prudence adaptés à vos réponses.";
      }


      if (summaryProfile) {
        summaryProfile.hidden =
          safetyTriggered;
      }


      if (summaryType) {
        summaryType.textContent =
          profileTypeText;
      }


      if (summaryProfileLabel) {
        summaryProfileLabel.textContent =
          profile.label;
      }


      const safetyReasonLabels = {
        painful_hot_swollen:
          "Zone très douloureuse, chaude ou fortement gonflée",

        pus_discharge:
          "Pus ou écoulement inhabituel",

        burn_blisters:
          "Cloques ou brûlure importante",

        persistent_48h:
          "Rougeur ou gonflement persistant depuis plus de 48 h",

        systemic_symptoms:
          "Fièvre ou malaise général"
      };


      if (summaryEyebrow) {
        summaryEyebrow.textContent =
          safetyTriggered
            ? "REPÈRES DE PRUDENCE"
            : "SYNTHÈSE DU BILAN";
      }


      if (summaryListLabel) {
        summaryListLabel.textContent =
          safetyTriggered
            ? "CE QUE VOUS AVEZ SIGNALÉ"
            : profile.primary ===
                "BAL"
              ? "DIMENSIONS EN TÊTE"
              : "DIMENSIONS QUI RESSORTENT";
      }


      if (summaryCount) {
        if (safetyTriggered) {
          summaryCount.textContent =
            `${String(
              safetyReasonsForWeb.length
            ).padStart(
              2,
              "0"
            )} ${
              safetyReasonsForWeb.length ===
              1
                ? "SIGNAL"
                : "SIGNAUX"
            }`;
        } else {
          summaryCount.textContent =
            `${String(
              availableAxesForWeb.length
            ).padStart(
              2,
              "0"
            )} ${
              availableAxesForWeb.length ===
              1
                ? "AXE DISPONIBLE"
                : "AXES DISPONIBLES"
            }`;
        }
      }


      if (summaryList) {
        summaryList.replaceChildren();

        summaryList.classList.toggle(
          "is-safety",
          safetyTriggered
        );

        const itemsToRender =
          safetyTriggered
            ? safetyReasonsForWeb.map(
                (
                  reason,
                  index
                ) => ({
                  label:
                    safetyReasonLabels[
                      reason
                    ] ??
                    String(
                      reason
                    ),

                  value:
                    String(
                      index + 1
                    ).padStart(
                      2,
                      "0"
                    )
                })
              )
            : leadAxesForWeb.map(
                (item) => ({
                  label:
                    axisLabels[
                      item.key
                    ],

                  value:
                    `${item.score}/100`
                })
              );

        itemsToRender.forEach(
          (item) => {
            const row =
              document.createElement(
                "div"
              );

            row.className =
              "beryt-bilan__result-summary-item";

            const label =
              document.createElement(
                "span"
              );

            label.textContent =
              item.label;

            const value =
              document.createElement(
                "strong"
              );

            value.textContent =
              item.value;

            row.append(
              label,
              value
            );

            summaryList.appendChild(
              row
            );
          }
        );
      }


      const axesSummaryLabelText =
        safetyTriggered
          ? "DIMENSIONS LES PLUS MARQUÉES"
          : profile.primary ===
              "BAL"
            ? "LECTURE ÉQUILIBRÉE"
            : availableAxesForWeb.length <
                axisOrder.length
              ? "LECTURE PARTIELLE"
              : "LES SCORES LES PLUS ÉLEVÉS";


      const axesSummaryNamesText =
        leadAxesForWeb.length >
        0
          ? leadAxesForWeb
              .map(
                (item) =>
                  axisLabels[
                    item.key
                  ]
              )
              .join(
                " · "
              )
          : "Aucune dimension disponible";


      const axesSummaryNoteText =
        safetyTriggered
          ? "Ces dimensions ressortent davantage dans vos réponses. Elles restent descriptives et ne remplacent pas l’évaluation des signes que vous avez signalés."
          : profile.primary ===
              "BAL"
            ? "Aucune dimension ne domine nettement l’ensemble de votre Bilan."
            : availableAxesForWeb.length <
                axisOrder.length
              ? "La lecture est construite uniquement à partir des axes disponibles."
              : "Ces dimensions obtiennent les scores les plus élevés dans votre Bilan.";


      if (axesSummaryLabel) {
        axesSummaryLabel.textContent =
          axesSummaryLabelText;
      }

      if (axesSummaryNames) {
        axesSummaryNames.textContent =
          axesSummaryNamesText;
      }

      if (axesSummaryNote) {
        axesSummaryNote.textContent =
          axesSummaryNoteText;
      }
      

const insightsList =
  bilan.querySelector(
    "[data-bilan-insights-list]"
  );

const sortedInsights =
  [...insights]
    .filter(
      (insight) =>
        insight &&
        typeof insight.text === "string" &&
        insight.text.trim().length > 0
    )
    .sort(
      (a, b) =>
        Number(a.rank) -
        Number(b.rank)
    )
    .slice(0, 3);

const getInsightLabel = (
  insightId
) => {
  const id =
    String(
      insightId ?? ""
    );

  if (
    id === "PRIORITY_ALIGNED" ||
    id === "PRIORITY_DIVERGES" ||
    id === "PRIORITY_UNDERSTANDING" ||
    id === "PRIORITY_BALANCED"
  ) {
    return "VOTRE PRIORITÉ";
  }

  if (
    id.endsWith(
      "_COMBINATION"
    )
  ) {
    return "INTERACTION";
  }

  if (
    id.endsWith(
      "_SECONDARY"
    )
  ) {
    return "DIMENSION SECONDAIRE";
  }

  if (
    id.endsWith(
      "_CONTEXT"
    )
  ) {
    return "POINT D’ATTENTION";
  }

  if (
    id.endsWith(
      "_DOMINANT"
    ) ||
    id === "BALANCED_PROFILE"
  ) {
    return "SIGNAL PRINCIPAL";
  }

  return "POINT DE LECTURE";
};

const insightsSection =
  insightsList?.closest(
    ".beryt-bilan__result-insights"
  );

if (insightsSection) {
  insightsSection.hidden =
    safetyTriggered ||
    sortedInsights.length === 0;
}

if (
  insightsList &&
  !safetyTriggered
) {
  insightsList.replaceChildren();

  sortedInsights.forEach(
    (
      insight,
      index
    ) => {
      const card =
        document.createElement(
          "article"
        );

      card.className =
        "beryt-bilan__insight-card";

      if (index === 0) {
        card.classList.add(
          "beryt-bilan__insight-card--primary"
        );
      }

      card.dataset.bilanInsightId =
        String(
          insight.id ?? ""
        );

      const meta =
        document.createElement(
          "div"
        );

      meta.className =
        "beryt-bilan__insight-meta";

      const number =
        document.createElement(
          "span"
        );

      number.className =
        "beryt-bilan__insight-number";

      number.textContent =
        String(
          index + 1
        ).padStart(
          2,
          "0"
        );

      const type =
        document.createElement(
          "span"
        );

      type.className =
        "beryt-bilan__insight-type";

      type.textContent =
        getInsightLabel(
          insight.id
        );

      const copy =
        document.createElement(
          "div"
        );

      copy.className =
        "beryt-bilan__insight-copy";

      const firstBreak =
        insight.text.indexOf(
          ". "
        );

      const leadText =
        firstBreak >= 0
          ? insight.text.slice(
              0,
              firstBreak + 1
            )
          : insight.text;

      const detailText =
        firstBreak >= 0
          ? insight.text
              .slice(
                firstBreak + 2
              )
              .trim()
          : "";

      const lead =
        document.createElement(
          "p"
        );

      lead.className =
        "beryt-bilan__insight-lead";

      lead.textContent =
        leadText;

      copy.appendChild(
        lead
      );

      if (detailText) {
        const detail =
          document.createElement(
            "p"
          );

        detail.className =
          "beryt-bilan__insight-detail";

        detail.textContent =
          detailText;

        copy.appendChild(
          detail
        );
      }

      meta.append(
        number,
        type
      );

      card.append(
        meta,
        copy
      );

      insightsList.appendChild(
        card
      );
    }
  );
}


const recommendations =
  result?.recommendations ?? {};

const safetyRecommendationsForWeb =
  safetyTriggered
    ? Object.values(
        recommendations
      )
        .flatMap(
          (items) =>
            Array.isArray(items)
              ? items
              : []
        )
        .filter(
          (item) =>
            typeof item?.id ===
              "string" &&
            item.id.startsWith(
              "SAFETY_"
            ) &&
            typeof item?.text ===
              "string" &&
            item.text.trim().length >
              0
        )
        .sort(
          (a, b) =>
            Number(
              b.priority
            ) -
            Number(
              a.priority
            )
        )
    : [];

const routineSlotLabels = {
  just_after:
    "JUSTE APRÈS",

  following_days:
    "JOURS SUIVANTS",

  between_sessions:
    "ENTRE DEUX ÉPILATIONS",

  before_next:
    "AVANT LA PROCHAINE"
};

const routineSection =
  bilan.querySelector(
    "[data-bilan-result-routine-section]"
  );

      const routineTitle =
        bilan.querySelector(
          "[data-bilan-result-routine-title]"
        );

      const routineIntro =
        bilan.querySelector(
          "[data-bilan-result-routine-intro]"
        );

      const routineSteps =
        Array.from(
          bilan.querySelectorAll(
            "[data-bilan-recommendation-slot]"
          )
        );

      const adjustmentsBlock =
        bilan.querySelector(
          "[data-bilan-routine-adjustments]"
        );

      const adjustmentsList =
        bilan.querySelector(
          "[data-bilan-routine-adjustments-list]"
        );

      let renderedRecommendationCount =
        0;

      let renderedRoutineStepCount =
        0;

      const routineTimeline =
        bilan.querySelector(
          ".beryt-bilan__routine-timeline"
        );

      routineSteps.forEach(
        (
          step,
          stepIndex
        ) => {
          const slot =
            step.dataset
              .bilanRecommendationSlot;

          const list =
            step.querySelector(
              "[data-bilan-recommendation-list]"
            );

          if (!list || !slot) {
            return;
          }

          list.replaceChildren();

          const items =
            safetyTriggered
              ? safetyRecommendationsForWeb[
                  stepIndex
                ]
                ? [
                    safetyRecommendationsForWeb[
                      stepIndex
                    ]
                  ]
                : []
              : Array.isArray(
                  recommendations?.[slot]
                )
                ? recommendations[
                    slot
                  ]
                : [];

          const validItems =
            items.filter(
              (item) =>
                typeof item?.text ===
                  "string" &&
                item.text.trim().length >
                  0
            );

          const isEmpty =
            validItems.length === 0;

          step.classList.toggle(
            "is-empty",
            isEmpty
          );

          step.hidden =
            isEmpty;

          if (isEmpty) {
            return;
          }

          renderedRoutineStepCount +=
            1;

          const stepNumber =
            step.querySelector(
              ".beryt-bilan__routine-step-number"
            );

          if (stepNumber) {
            stepNumber.textContent =
              String(
                renderedRoutineStepCount
              ).padStart(
                2,
                "0"
              );
          }

          const stepLabel =
            step.querySelector(
              ".beryt-bilan__routine-step-label"
            );

          if (stepLabel) {
            if (safetyTriggered) {
              stepLabel.textContent =
                renderedRoutineStepCount ===
                1
                  ? "À RETENIR MAINTENANT"
                  : "EN ATTENDANT";
            } else {
              stepLabel.textContent =
                routineSlotLabels[
                  slot
                ] ?? "";
            }
          }

          validItems.forEach(
            (item) => {
              const recommendation =
                document.createElement(
                  "div"
                );

              recommendation.className =
                "beryt-bilan__routine-recommendation";

              const marker =
                document.createElement(
                  "span"
                );

              marker.className =
                "beryt-bilan__routine-recommendation-marker";

              marker.setAttribute(
                "aria-hidden",
                "true"
              );

              const copy =
                document.createElement(
                  "div"
                );

              copy.className =
                "beryt-bilan__routine-copy";

              const firstBreak =
                item.text.indexOf(
                  ". "
                );

              const leadText =
                firstBreak >= 0
                  ? item.text.slice(
                      0,
                      firstBreak + 1
                    )
                  : item.text;

              const detailText =
                firstBreak >= 0
                  ? item.text
                      .slice(
                        firstBreak + 2
                      )
                      .trim()
                  : "";

              const lead =
                document.createElement(
                  "p"
                );

              lead.className =
                "beryt-bilan__routine-lead";

              lead.textContent =
                leadText;

              copy.appendChild(
                lead
              );

              if (detailText) {
                const detail =
                  document.createElement(
                    "p"
                  );

                detail.className =
                  "beryt-bilan__routine-detail";

                detail.textContent =
                  detailText;

                copy.appendChild(
                  detail
                );
              }

              recommendation.append(
                marker,
                copy
              );

              list.appendChild(
                recommendation
              );

              renderedRecommendationCount +=
                1;
            }
          );
        }
      );

      if (routineTimeline) {
        routineTimeline.dataset
          .visibleSteps =
          String(
            renderedRoutineStepCount
          );
      }

      const adjustments =
        safetyTriggered
          ? []
          : Array.isArray(
              recommendations
                ?.adjustments
            )
            ? recommendations
                .adjustments
            : [];

      if (
        adjustmentsBlock &&
        adjustmentsList
      ) {
        adjustmentsList.replaceChildren();

        const validAdjustments =
          adjustments.filter(
            (item) =>
              typeof item?.text ===
                "string" &&
              item.text.trim().length >
                0
          );

        validAdjustments.forEach(
          (item) => {
            const row =
              document.createElement(
                "div"
              );

            row.className =
              "beryt-bilan__routine-adjustment";

            const marker =
              document.createElement(
                "span"
              );

            marker.className =
              "beryt-bilan__routine-adjustment-marker";

            marker.setAttribute(
              "aria-hidden",
              "true"
            );

            const text =
              document.createElement(
                "p"
              );

            text.textContent =
              item.text;

            row.append(
              marker,
              text
            );

            adjustmentsList.appendChild(
              row
            );

            renderedRecommendationCount +=
              1;
          }
        );

        adjustmentsBlock.hidden =
          validAdjustments.length ===
          0;
      }

      if (
        routineTitle &&
        routineIntro
      ) {
        if (safetyTriggered) {
          routineTitle.textContent =
            "Les repères à retenir.";

          routineIntro.textContent =
            "Les signes que vous avez signalés passent avant les recommandations cosmétiques habituelles. Les repères de prudence adaptés à vos réponses sont présentés en priorité.";
        } else {
          routineTitle.textContent =
            "Vos repères personnalisés.";

          routineIntro.textContent =
            renderedRecommendationCount ===
            1
              ? "Un repère personnalisé a été retenu à partir de vos réponses."
              : renderedRecommendationCount ===
                  2
                ? "Deux repères personnalisés ont été retenus à partir de vos réponses. Ils sont organisés selon le moment où ils peuvent vous être utiles."
                : `${renderedRecommendationCount} repères personnalisés ont été retenus à partir de vos réponses. Ils sont organisés selon le moment où ils peuvent vous être utiles.`;
        }
      }

      if (routineSection) {
        routineSection.hidden =
          renderedRecommendationCount ===
          0;
      }

      const axesSection =
        bilan.querySelector(
          ".beryt-bilan__result-axes"
        );

      const routineIndex =
        routineSection?.querySelector(
          ".beryt-bilan__result-section-index"
        );

      const axesIndex =
        axesSection?.querySelector(
          ".beryt-bilan__result-section-index"
        );

      /*
       * Safety :
       * Hero
       * Repères de prudence
       * Dimensions
       *
       * Standard :
       * Dimensions
       * Insights
       * Repères personnalisés
       */

      if (
        routineSection &&
        axesSection
      ) {
        if (safetyTriggered) {
          axesSection.insertAdjacentElement(
            "beforebegin",
            routineSection
          );
        } else if (
          insightsSection
        ) {
          insightsSection.insertAdjacentElement(
            "afterend",
            routineSection
          );
        }
      }

      if (routineIndex) {
        routineIndex.textContent =
          safetyTriggered
            ? "02 — REPÈRES DE PRUDENCE"
            : "04 — VOS REPÈRES";
      }

      if (axesIndex) {
        axesIndex.textContent =
          safetyTriggered
            ? "03 — VOS DIMENSIONS"
            : "02 — VOTRE BILAN EN UN COUP D’ŒIL";
      }

      const existingDailyProfileSection =
        bilan.querySelector(
          "[data-bilan-result-daily-profile]"
        );

      existingDailyProfileSection?.remove();

      const dailyProfileItems =
        Array.isArray(
          result?.daily_profile
        )
          ? result.daily_profile.filter(
              (item) =>
                item &&
                typeof item.title ===
                  "string" &&
                item.title.trim().length >
                  0 &&
                typeof item.text ===
                  "string" &&
                item.text.trim().length >
                  0
            )
          : [];

      let dailyProfileSection =
        null;

      if (
        !safetyTriggered &&
        dailyProfileItems.length >
          0 &&
        routineSection
      ) {
        dailyProfileSection =
          document.createElement(
            "section"
          );

        dailyProfileSection.className =
          "beryt-bilan__result-daily-profile";

        dailyProfileSection.dataset
          .bilanResultDailyProfile =
          "";

        const dailyHead =
          document.createElement(
            "div"
          );

        dailyHead.className =
          "beryt-bilan__result-section-head";

        const dailyHeadingMain =
          document.createElement(
            "div"
          );

        const dailyIndex =
          document.createElement(
            "p"
          );

        dailyIndex.className =
          "beryt-bilan__result-section-index";

        const dailyProfileSectionNumber =
          renderedRecommendationCount >
          0
            ? 5
            : 4;

        dailyIndex.textContent =
          `${String(
            dailyProfileSectionNumber
          ).padStart(
            2,
            "0"
          )} — AU QUOTIDIEN`;

        const dailyTitle =
          document.createElement(
            "h2"
          );

        dailyTitle.className =
          "beryt-bilan__result-section-title";

        dailyTitle.textContent =
          "Ce que votre Bilan signifie au quotidien.";

        dailyHeadingMain.append(
          dailyIndex,
          dailyTitle
        );

        const dailyIntro =
          document.createElement(
            "p"
          );

        dailyIntro.className =
          "beryt-bilan__result-section-intro";
        dailyIntro.textContent =
          dailyProfileItems.length ===
          1
            ? "Ce repère traduit un élément de votre Bilan en un point simple à observer entre vos prochaines séances."
            : "Ces repères traduisent les principaux éléments de votre Bilan en points simples à observer entre vos prochaines séances.";

        dailyHead.append(
          dailyHeadingMain,
          dailyIntro
        );

        const dailyList =
          document.createElement(
            "div"
          );

        dailyList.className =
          "beryt-bilan__daily-profile-list";

        dailyProfileItems.forEach(
          (
            item,
            index
          ) => {
            const dailyItem =
              document.createElement(
                "article"
              );

            dailyItem.className =
              "beryt-bilan__daily-profile-item";

            dailyItem.dataset
              .bilanDailyProfileId =
              String(
                item.id ?? ""
              );

            const number =
              document.createElement(
                "span"
              );

            number.className =
              "beryt-bilan__daily-profile-number";

            number.textContent =
              String(
                item.rank ??
                  index + 1
              ).padStart(
                2,
                "0"
              );

            const itemTitle =
              document.createElement(
                "h3"
              );

            itemTitle.className =
              "beryt-bilan__daily-profile-title";

            itemTitle.textContent =
              item.title;

            const itemText =
              document.createElement(
                "p"
              );

            itemText.className =
              "beryt-bilan__daily-profile-text";

            itemText.textContent =
              item.text;

            dailyItem.append(
              number,
              itemTitle,
              itemText
            );

            dailyList.appendChild(
              dailyItem
            );
          }
        );

        dailyProfileSection.append(
          dailyHead,
          dailyList
        );

        routineSection.insertAdjacentElement(
          "afterend",
          dailyProfileSection
        );
      }

      const existingTransparencySection =
        bilan.querySelector(
          "[data-bilan-result-transparency]"
        );

      existingTransparencySection?.remove();

      const evidenceSources =
        Array.isArray(
          result?.evidence_sources
        )
          ? result.evidence_sources
          : [];

      if (
        evidenceSources.length > 0 &&
        routineSection
      ) {
        const sourceTypeLabels = {
          experimental_study:
            "Étude expérimentale",

          human_study:
            "Étude chez l’humain",

          human_controlled_trial:
            "Essai contrôlé chez l’humain",

          human_comparative_study:
            "Étude comparative chez l’humain",

          regulatory_document:
            "Document réglementaire",

          clinical_guidance:
            "Recommandation clinique"
        };

        const sourceTypes =
          new Set(
            evidenceSources.map(
              (source) =>
                source.source_type
            )
          );

        let interpretationText =
          "Ces sources éclairent les recommandations proposées, mais elles n’étudient pas nécessairement exactement votre situation personnelle.";

        const hasExperimental =
          sourceTypes.has(
            "experimental_study"
          );

        const hasHuman =
          [
            "human_study",
            "human_controlled_trial",
            "human_comparative_study"
          ].some(
            (type) =>
              sourceTypes.has(type)
          );

        const hasClinicalGuidance =
          sourceTypes.has(
            "clinical_guidance"
          );

        const hasRegulatory =
          sourceTypes.has(
            "regulatory_document"
          );

        if (
          hasExperimental &&
          hasHuman
        ) {
          interpretationText =
            "Les sources combinent ici des données expérimentales et des observations chez l’humain. Elles éclairent le conseil, sans reproduire exactement votre situation personnelle.";
        } else if (
          hasClinicalGuidance
        ) {
          interpretationText =
            "Ces références servent ici à orienter la prudence face aux signes que vous avez signalés. Elles ne permettent pas à BERYT de poser un diagnostic.";
        } else if (
          hasRegulatory
        ) {
          interpretationText =
            "Ces références décrivent des consignes propres à un dispositif ou à un produit. Elles ne doivent pas être appliquées indistinctement à tous les appareils ou toutes les formules.";
        } else if (
          hasHuman
        ) {
          interpretationText =
            "Ces sources reposent sur des données chez l’humain, mais elles n’étudient pas forcément la même zone, la même méthode d’épilation ou le même contexte que le vôtre.";
        } else if (
          hasExperimental
        ) {
          interpretationText =
            "Une partie des données vient d’un contexte expérimental. Elles aident à comprendre ce qui peut se passer au niveau de la peau, sans constituer à elles seules une règle clinique universelle.";
        }

        const limitations =
          evidenceSources.flatMap(
            (source) =>
              Array.isArray(
                source.limitations
              )
                ? source.limitations
                : []
          );

        const normalizedLimitations =
          limitations.join(
            " "
          ).toLowerCase();

        const publicLimits =
          [];

        if (
          /délai|calendrier|universel/.test(
            normalizedLimitations
          )
        ) {
          publicLimits.push(
            "Elles ne permettent pas de fixer un délai universel après l’épilation."
          );
        }

        if (
          /axillaire|barbe|zone|jambes|maillot|aisselles/.test(
            normalizedLimitations
          )
        ) {
          publicLimits.push(
            "Elles ne permettent pas non plus de généraliser automatiquement les résultats à toutes les zones du corps."
          );
        }

        if (
          /dispositif|appareil|réglage|intervalle/.test(
            normalizedLimitations
          )
        ) {
          publicLimits.push(
            "Elles ne permettent pas de déduire des réglages ou des intervalles valables pour tous les dispositifs."
          );
        }

        if (
          /diagnostic|infection|brûlure|profondeur|gravité/.test(
            normalizedLimitations
          )
        ) {
          publicLimits.push(
            "Elles ne permettent pas à BERYT de poser un diagnostic ni d’évaluer à distance la gravité d’une lésion."
          );
        }

        const limitationText =
          publicLimits.length > 0
            ? [
                ...new Set(
                  publicLimits
                )
              ]
                .slice(
                  0,
                  2
                )
                .join(
                  " "
                )
            : "Elles ne permettent pas de transformer ce repère en règle universelle valable pour toutes les peaux et toutes les méthodes d’épilation.";

        const transparencySection =
          document.createElement(
            "section"
          );

        transparencySection.className =
          "beryt-bilan__result-transparency";

        transparencySection.dataset
          .bilanResultTransparency =
          "";

        const head =
          document.createElement(
            "div"
          );

        head.className =
          "beryt-bilan__result-section-head";

        const headingMain =
          document.createElement(
            "div"
          );

        const index =
          document.createElement(
            "p"
          );

        index.className =
          "beryt-bilan__result-section-index";

        const transparencySectionNumber =
          safetyTriggered
            ? 4
            : 4 +
              (
                renderedRecommendationCount >
                0
                  ? 1
                  : 0
              ) +
              (
                dailyProfileItems.length >
                0
                  ? 1
                  : 0
              );

        index.textContent =
          `${String(
            transparencySectionNumber
          ).padStart(
            2,
            "0"
          )} — TRANSPARENCE`;

        const title =
          document.createElement(
            "h2"
          );

        title.className =
          "beryt-bilan__result-section-title";

        title.textContent =
          safetyTriggered
            ? "D’où viennent ces repères de prudence."
            : "D’où vient cette lecture.";

        headingMain.append(
          index,
          title
        );

        const intro =
          document.createElement(
            "p"
          );

        intro.className =
          "beryt-bilan__result-section-intro";

                intro.textContent =
          safetyTriggered
            ? "Les repères de prudence présentés ici s’appuient sur les références réellement mobilisées pour votre résultat, avec leurs limites."
            : "Les éléments de votre Bilan sont mis en perspective avec les références réellement mobilisées pour votre résultat, avec leurs limites."; 

        head.append(
          headingMain,
          intro
        );

        const sourcesBlock =
          document.createElement(
            "details"
          );

        sourcesBlock.className =
          "beryt-bilan__transparency-sources";

        sourcesBlock.open =
          false;

        const sourcesHead =
          document.createElement(
            "summary"
          );

        sourcesHead.className =
          "beryt-bilan__transparency-sources-head";

        const sourcesLabel =
          document.createElement(
            "span"
          );

        sourcesLabel.textContent =
          "VOIR LES RÉFÉRENCES";

        const sourcesCount =
          document.createElement(
            "strong"
          );

        sourcesCount.textContent =
          `${evidenceSources.length} ${
            evidenceSources.length ===
            1
              ? "référence"
              : "références"
          }`;

        sourcesHead.append(
          sourcesLabel,
          sourcesCount
        );

        sourcesBlock.appendChild(
          sourcesHead
        );

        evidenceSources.forEach(
          (
            source,
            sourceIndex
          ) => {
            const sourceRow =
              document.createElement(
                "article"
              );

            sourceRow.className =
              "beryt-bilan__transparency-source";

            const sourceNumber =
              document.createElement(
                "span"
              );

            sourceNumber.className =
              "beryt-bilan__transparency-source-number";

            sourceNumber.textContent =
              String(
                sourceIndex + 1
              ).padStart(
                2,
                "0"
              );

            const sourceContent =
              document.createElement(
                "div"
              );

            sourceContent.className =
              "beryt-bilan__transparency-source-content";

            const sourceMeta =
              document.createElement(
                "p"
              );

            sourceMeta.className =
              "beryt-bilan__transparency-source-meta";

            sourceMeta.textContent =
              `${
                sourceTypeLabels[
                  source.source_type
                ] ??
                "Source documentaire"
              } · ${source.year}`;

            const sourceTitle =
              document.createElement(
                "p"
              );

            sourceTitle.className =
              "beryt-bilan__transparency-source-title";

            sourceTitle.textContent =
              source.title;

            const sourceLocator =
              document.createElement(
                "p"
              );

            sourceLocator.className =
              "beryt-bilan__transparency-source-locator";

            sourceLocator.textContent =
              String(
                source.locator ?? ""
              ).replace(
                /\s*\|\s*/g,
                " · "
              );

            sourceContent.append(
              sourceMeta,
              sourceTitle,
              sourceLocator
            );

            sourceRow.append(
              sourceNumber,
              sourceContent
            );

            sourcesBlock.appendChild(
              sourceRow
            );
          }
        );

        const interpretation =
          document.createElement(
            "div"
          );

        interpretation.className =
          "beryt-bilan__transparency-interpretation";

        const createNote = (
          label,
          text
        ) => {
          const note =
            document.createElement(
              "div"
            );

          note.className =
            "beryt-bilan__transparency-note";

          const noteLabel =
            document.createElement(
              "span"
            );

          noteLabel.className =
            "beryt-bilan__transparency-note-label";

          noteLabel.textContent =
            label;

          const noteText =
            document.createElement(
              "p"
            );

          noteText.textContent =
            text;

          note.append(
            noteLabel,
            noteText
          );

          return note;
        };

        interpretation.append(
          createNote(
            "COMMENT LES LIRE",
            interpretationText
          ),

          createNote(
            "CE QU’ELLES NE PERMETTENT PAS DE CONCLURE",
            limitationText
          )
        );

        transparencySection.append(
          head,
          sourcesBlock,
          interpretation
        );

        const transparencyAnchor =
          safetyTriggered
            ? axesSection ??
              routineSection
            : dailyProfileSection ??
              routineSection;

        transparencyAnchor.insertAdjacentElement(
          "afterend",
          transparencySection
        );
      }


      /* =========================
         POUR ALLER PLUS LOIN
      ========================== */

      const existingFurtherReadingSection =
        bilan.querySelector(
          "[data-bilan-result-further-reading]"
        );

      existingFurtherReadingSection?.remove();

      const furtherReadingContent = {
        regrowth: {
          eyebrow:
            "REPOUSSE & SCIENCE",

          title:
            "Mieux comprendre ce qui influence la repousse",

          text:
            "Cycle du poil, rythme de retour et facteurs qui modifient la perception de la repousse après l’épilation.",

          href:
            "/pages/ralentir-repousse"
        },

        ingrown: {
          eyebrow:
            "POILS INCARNÉS",

          title:
            "Comprendre pourquoi les poils s’incarnent",

          text:
            "Mieux identifier les mécanismes, les facteurs favorisants et les gestes qui peuvent compter entre deux épilations.",

          href:
            "/pages/poils-incarnes"
        },

        after_epilation: {
          eyebrow:
            "APRÈS L’ÉPILATION",

          title:
            "Mieux comprendre les réactions après l’épilation",

          text:
            "Rougeurs, picotements, démangeaisons ou inconfort : comprendre ce qui peut se passer juste après une séance.",

          href:
            "/pages/apres-epilation"
        },

        sensitivity: {
          eyebrow:
            "ZONES & PEAUX SENSIBLES",

          title:
            "Comprendre les besoins d’une peau plus sensible",

          text:
            "Toutes les zones ne réagissent pas de la même manière. Découvrez comment le contexte cutané peut modifier l’expérience de l’épilation.",

          href:
            "/pages/zones-peaux-sensibles"
        },

        methods: {
          eyebrow:
            "MÉTHODES D’ÉPILATION",

          title:
            "Comparer les différentes méthodes d’épilation",

          text:
            "Rasoir, cire, épilateur, crème dépilatoire, IPL ou laser : comprendre ce qui les distingue et ce qu’elles impliquent pour la peau.",

          href:
            "/pages/methodes-epilation"
        },

        guide: {
          eyebrow:
            "LE GUIDE BERYT",

          title:
            "Explorer l’épilation autrement",

          text:
            "Repousse, peau, méthodes, actifs et après-épilation : poursuivez votre exploration à partir des sujets qui vous intéressent.",

          href:
            "/blogs/guide-peau-repousse"
        }
      };

      const furtherReadingItems =
        Array.isArray(
          result?.further_reading
        )
          ? result.further_reading
              .filter(
                (item) =>
                  item &&
                  furtherReadingContent[
                    item.topic
                  ]
              )
              .sort(
                (a, b) =>
                  Number(a.rank) -
                  Number(b.rank)
              )
              .slice(
                0,
                3
              )
          : [];

      if (
        !safetyTriggered &&
        furtherReadingItems.length >
          0 &&
        routineSection
      ) {
        const furtherReadingSection =
          document.createElement(
            "section"
          );

        furtherReadingSection.className =
          "beryt-bilan__result-further-reading";

        furtherReadingSection.dataset
          .bilanResultFurtherReading =
          "";

        const furtherHead =
          document.createElement(
            "div"
          );

        furtherHead.className =
          "beryt-bilan__result-section-head";

        const furtherHeadingMain =
          document.createElement(
            "div"
          );

        const furtherIndex =
          document.createElement(
            "p"
          );

        furtherIndex.className =
          "beryt-bilan__result-section-index";

        const renderedTransparencySection =
          bilan.querySelector(
            "[data-bilan-result-transparency]"
          );

        const furtherReadingSectionNumber =
          4 +
          (
            renderedRecommendationCount >
            0
              ? 1
              : 0
          ) +
          (
            dailyProfileItems.length >
            0
              ? 1
              : 0
          ) +
          (
            renderedTransparencySection
              ? 1
              : 0
          );

        furtherIndex.textContent =
          `${String(
            furtherReadingSectionNumber
          ).padStart(
            2,
            "0"
          )} — À APPROFONDIR`;

        const furtherTitle =
          document.createElement(
            "h2"
          );

        furtherTitle.className =
          "beryt-bilan__result-section-title";

        furtherTitle.textContent =
          "Approfondir ce qui compte pour vous.";

        furtherHeadingMain.append(
          furtherIndex,
          furtherTitle
        );

        const furtherIntro =
          document.createElement(
            "p"
          );

        furtherIntro.className =
          "beryt-bilan__result-section-intro";

        furtherIntro.textContent =
          furtherReadingItems.length ===
          1
            ? "Cette piste de lecture a été sélectionnée à partir de votre Bilan pour vous permettre d’approfondir le sujet qui compte le plus dans votre situation."
            : "Ces pistes de lecture ont été sélectionnées à partir de vos priorités, de votre profil et de votre contexte pour vous orienter vers les contenus BERYT les plus utiles.";

        furtherHead.append(
          furtherHeadingMain,
          furtherIntro
        );

        const furtherList =
          document.createElement(
            "div"
          );

        furtherList.className =
          "beryt-bilan__further-reading-list";

        furtherReadingItems.forEach(
          (
            item,
            index
          ) => {
            const content =
              furtherReadingContent[
                item.topic
              ];

            const link =
              document.createElement(
                "a"
              );

            link.className =
              "beryt-bilan__further-reading-item";

            if (index === 0) {
              link.classList.add(
                "is-primary"
              );
            }

            link.href =
              content.href;

            link.dataset
              .bilanFurtherReadingTopic =
              item.topic;

            const number =
              document.createElement(
                "span"
              );

            number.className =
              "beryt-bilan__further-reading-number";

            number.textContent =
              String(
                item.rank ??
                  index + 1
              ).padStart(
                2,
                "0"
              );

            const heading =
              document.createElement(
                "div"
              );

            heading.className =
              "beryt-bilan__further-reading-heading";

            const meta =
              document.createElement(
                "span"
              );

            meta.className =
              "beryt-bilan__further-reading-meta";

            if (
              item.origin ===
                "priority" &&
              item.priority_rank
            ) {
              meta.textContent =
                `VOTRE PRIORITÉ ${String(
                  item.priority_rank
                ).padStart(
                  2,
                  "0"
                )}`;
            } else if (
              item.origin ===
              "profile"
            ) {
              meta.textContent =
                "LIÉ À VOTRE PROFIL";
            } else {
              meta.textContent =
                "À EXPLORER";
            }

            const eyebrow =
              document.createElement(
                "span"
              );

            eyebrow.className =
              "beryt-bilan__further-reading-eyebrow";

            eyebrow.textContent =
              content.eyebrow;

            const itemTitle =
              document.createElement(
                "h3"
              );

            itemTitle.className =
              "beryt-bilan__further-reading-title";

            itemTitle.textContent =
              content.title;

            heading.append(
              meta,
              eyebrow,
              itemTitle
            );

            const itemText =
              document.createElement(
                "p"
              );

            itemText.className =
              "beryt-bilan__further-reading-text";

            itemText.textContent =
              content.text;

            const arrow =
              document.createElement(
                "span"
              );

            arrow.className =
              "beryt-bilan__further-reading-arrow";

            arrow.setAttribute(
              "aria-hidden",
              "true"
            );

            arrow.textContent =
              "→";

            link.append(
              number,
              heading,
              itemText,
              arrow
            );

            furtherList.appendChild(
              link
            );
          }
        );

        furtherReadingSection.append(
          furtherHead,
          furtherList
        );

        const furtherReadingAnchor =
          renderedTransparencySection ??
          dailyProfileSection ??
          routineSection;

        furtherReadingAnchor.insertAdjacentElement(
          "afterend",
          furtherReadingSection
        );
      }


      const levelLabels = {
        low: "FAIBLE",
        moderate: "MODÉRÉ",
        marked: "MARQUÉ",
        high: "ÉLEVÉ",
        very_high: "TRÈS ÉLEVÉ",
        unavailable: "NON DISPONIBLE"
      };

      const axisRows =
        Array.from(
          bilan.querySelectorAll(
            "[data-bilan-axis]"
          )
        );

      axisRows.forEach(
        (row) => {
          const axisKey =
            row.dataset.bilanAxis;

          const axis =
            axes[axisKey];

          const scoreElement =
            row.querySelector(
              "[data-bilan-axis-score]"
            );

          const levelElement =
            row.querySelector(
              "[data-bilan-axis-level]"
            );

          const barElement =
            row.querySelector(
              "[data-bilan-axis-bar]"
            );

          if (
            !axis ||
            !scoreElement ||
            !levelElement ||
            !barElement
          ) {
            return;
          }

          const hasScore =
            typeof axis.score ===
            "number";

          const normalizedScore =
            hasScore
              ? Math.max(
                  0,
                  Math.min(
                    100,
                    axis.score
                  )
                )
              : 0;

          scoreElement.textContent =
            hasScore
              ? Math.round(
                  normalizedScore
                )
              : "—";

          let displayLevel =
            levelLabels[
              axis.level
            ] ??
            "—";

          if (
            axis.quality ===
              "partial" &&
            axis.level !==
              "unavailable"
          ) {
            displayLevel +=
              " · LECTURE PARTIELLE";
          }

          levelElement.textContent =
            displayLevel;

          barElement.style.width =
            `${normalizedScore}%`;

          row.classList.toggle(
            "is-unavailable",
            !hasScore
          );

          row.classList.toggle(
            "is-highlighted",
            leadAxisKeys.has(
              axisKey
            )
          );
        }
      );

      const radarAxes = [
        {
          key: "regrowth",
          x: 110,
          y: 20
        },
        {
          key: "density_thickness",
          x: 196,
          y: 82
        },
        {
          key: "reactivity",
          x: 163,
          y: 190
        },
        {
          key: "comfort_need",
          x: 57,
          y: 190
        },
        {
          key: "ingrown",
          x: 24,
          y: 82
        }
      ];

      const center = {
        x: 110,
        y: 110
      };

      const radarShape =
        bilan.querySelector(
          "[data-bilan-radar-shape]"
        );

      const radarNote =
        bilan.querySelector(
          "[data-bilan-radar-note]"
        );

      const radarPoints =
        [];

      let hasUnavailableAxis =
        false;

      radarAxes.forEach(
        (axisDefinition) => {
          const axis =
            axes[
              axisDefinition.key
            ];

          const hasScore =
            typeof axis?.score ===
            "number";

          if (!hasScore) {
            hasUnavailableAxis =
              true;
          }

          const normalizedScore =
            hasScore
              ? Math.max(
                  0,
                  Math.min(
                    100,
                    axis.score
                  )
                )
              : 0;

          const ratio =
            normalizedScore /
            100;

          const x =
            center.x +
            (
              axisDefinition.x -
              center.x
            ) *
              ratio;

          const y =
            center.y +
            (
              axisDefinition.y -
              center.y
            ) *
              ratio;

          radarPoints.push(
            `${x},${y}`
          );

          const point =
            bilan.querySelector(
              `[data-bilan-radar-point="${axisDefinition.key}"]`
            );

          if (point) {
            point.setAttribute(
              "cx",
              String(x)
            );

            point.setAttribute(
              "cy",
              String(y)
            );

            point.hidden =
              !hasScore;
          }
        }
      );

      if (radarShape) {
        radarShape.setAttribute(
          "points",
          radarPoints.join(" ")
        );

        radarShape.hidden =
          hasUnavailableAxis;
      }

      if (radarNote) {
        radarNote.textContent =
          hasUnavailableAxis
            ? "Une ou plusieurs dimensions ne disposent pas d’un score suffisant pour tracer une forme complète. Les points disponibles restent indiqués."
            : "Plus une pointe s’éloigne du centre, plus cette dimension ressort dans vos réponses.";
      }

      draftPhase =
        "result";

      clearBilanDraft();

      setExperienceMode(false);

      showScreen("result");

      setProgress(100);

      scrollToBilanTop();

      requestAnimationFrame(
        () => {
          profileLabel.focus({
            preventScroll: true
          });
        }
      );
    };


    /* =========================
       MAGIC LINK EMAIL
    ========================== */

    const cleanBilanMagicLinkUrl =
      () => {
        try {
          const url =
            new URL(
              window.location.href
            );

          /*
           * Le token BERYT est supprimé
           * immédiatement dès que le Bilan
           * a été récupéré avec succès.
           */

          url.searchParams.delete(
            "bilan_token"
          );

          url.searchParams.delete(
            "download"
          );

          window.history.replaceState(
            {},
            "",
            `${url.pathname}${url.search}${url.hash}`
          );

          /*
           * Klaviyo ajoute _kx aux liens
           * provenant de ses e-mails pour
           * son tracking onsite.
           *
           * On lui laisse un court instant
           * pour être lu par Klaviyo.js,
           * puis on nettoie également l'URL.
           */

          window.setTimeout(
            () => {
              try {
                const cleanUrl =
                  new URL(
                    window.location.href
                  );

                if (
                  !cleanUrl.searchParams.has(
                    "_kx"
                  )
                ) {
                  return;
                }

                cleanUrl.searchParams.delete(
                  "_kx"
                );

                window.history.replaceState(
                  {},
                  "",
                  `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`
                );
              } catch {
                // Aucun impact sur
                // l'utilisation du Bilan.
              }
            },
            1500
          );
        } catch {
          // Le Bilan reste fonctionnel
          // même si l'URL ne peut pas
          // être nettoyée.
        }
      };


    const recoverBilanFromMagicLink =
      async (
        recoveryToken,
        shouldDownloadPdf =
          false
      ) => {
        const normalizedToken =
          String(
            recoveryToken ?? ""
          ).trim();

        if (!normalizedToken) {
          return;
        }

        completedResultId =
          null;

        completedAccessToken =
          normalizedToken;

        completedResult =
          null;

        setExperienceMode(
          true
        );

        showScreen(
          "result-recovery"
        );

        setProgress(
          100
        );

        if (
          resultRecoveryError
        ) {
          resultRecoveryError.hidden =
            true;

          resultRecoveryError.textContent =
            "";
        }

        if (
          resultRecoveryResumeButton
        ) {
          resultRecoveryResumeButton.disabled =
            true;

          resultRecoveryResumeButton.innerHTML =
            'Chargement de votre Bilan <span aria-hidden="true">…</span>';
        }

        trackBilanEvent(
          "recovery_resumed",
          {
            recovery_type:
              "email_magic_link"
          }
        );

        try {
          const result =
            await loadBilanResult({
              allowUnknownResultId:
                true
            });

          /*
           * Le token recovery devient
           * maintenant la session active
           * de ce navigateur.
           */

          saveBilanResultSession(
            completedResultId,
            completedAccessToken,
            state.lead.first_name
          );

          /*
           * Le token ne doit pas rester
           * affiché dans la barre d'adresse
           * une fois la récupération réussie.
           */

          cleanBilanMagicLinkUrl();

          setExperienceMode(
            false
          );

          renderResultHero(
            result
          );

          trackBilanEvent(
            "result_viewed",
            {
              load_mode:
                "email_magic_link"
            }
          );

          /*
           * Report Link :
           * récupération du résultat,
           * puis téléchargement automatique.
           */

          if (
            shouldDownloadPdf
          ) {
            window.setTimeout(
              () => {
                downloadBilanPdf();
              },
              300
            );
          }
        } catch (error) {
          trackBilanEvent(
            "error",
            {
              error_stage:
                "email_magic_link",

              online:
                navigator.onLine
            }
          );

          console.error(
            "BERYT Bilan — erreur magic link",
            error
          );

          if (
            resultRecoveryError
          ) {
            resultRecoveryError.textContent =
              navigator.onLine
                ? "Nous n’avons pas pu ouvrir ce Bilan. Le lien est peut-être expiré ou n’est plus valide."
                : "Votre connexion semble interrompue. Reconnectez-vous puis rechargez cette page.";

            resultRecoveryError.hidden =
              false;
          }

          if (
            resultRecoveryResumeButton
          ) {
            resultRecoveryResumeButton.hidden =
              true;
          }
        }
      };


    const generationStageLabels =
      generationSteps.map(
        (step) =>
          step.querySelector(
            ".beryt-bilan__generation-step-label"
          )?.textContent?.trim() ??
          ""
      );

    let generationStageIndex =
      0;

    let generationTimer =
      null;

    let generationCountdownTimer =
      null;

    let generationStartedAt =
      0;

let generationAnalyticsMode =
  "fresh";

const GENERATION_TOTAL_DURATION_MS =
  5000;

const GENERATION_STEP_INTERVAL_MS =
  generationSteps.length > 0
    ? Math.round(
        GENERATION_TOTAL_DURATION_MS /
          generationSteps.length
      )
    : GENERATION_TOTAL_DURATION_MS;

    const updateGenerationStage = (
      nextIndex
    ) => {
      if (
        generationSteps.length ===
        0
      ) {
        return;
      }

      generationStageIndex =
        Math.max(
          0,
          Math.min(
            nextIndex,
            generationSteps.length -
              1
          )
        );

      generationSteps.forEach(
        (
          step,
          index
        ) => {
          step.classList.toggle(
            "is-active",
            index ===
              generationStageIndex
          );

          step.classList.toggle(
            "is-complete",
            index <
              generationStageIndex
          );
        }
      );

      if (
        generationCurrent
      ) {
        generationCurrent.textContent =
          generationStageLabels[
            generationStageIndex
          ] ?? "";
      }

      if (
        generationLine
      ) {
        const ratio =
          generationSteps.length >
          0
            ? (
                generationStageIndex +
                1
              ) /
              generationSteps.length
            : 1;

        generationLine.style.width =
          `${Math.round(
            ratio * 100
          )}%`;
      }



      if (
        generationStatus
      ) {
        generationStatus.textContent =
          "PRÉPARATION EN COURS";
      }


    };

    const updateGenerationCountdown =
      () => {
        if (
          !generationTime ||
          !generationStartedAt
        ) {
          return;
        }

        const elapsed =
          Date.now() -
          generationStartedAt;

        const remainingMs =
          Math.max(
            0,
            GENERATION_TOTAL_DURATION_MS -
              elapsed
          );

        const remainingSeconds =
          Math.max(
            1,
            Math.ceil(
              remainingMs /
                1000
            )
          );

        generationTime.textContent =
          `${String(
            remainingSeconds
          ).padStart(
            2,
            "0"
          )} s`;
      };


    const completeGenerationSequence =
      () => {
        if (
          generationTimer
        ) {
          window.clearInterval(
            generationTimer
          );

          generationTimer =
            null;
        }

        if (
          generationCountdownTimer
        ) {
          window.clearInterval(
            generationCountdownTimer
          );

          generationCountdownTimer =
            null;
        }

        generationStartedAt =
          0;

        generationSteps.forEach(
          (step) => {
            step.classList.remove(
              "is-active"
            );

            step.classList.add(
              "is-complete"
            );
          }
        );

        if (
          generationCurrent
        ) {
          generationCurrent.textContent =
            "Préparation terminée. Vous pouvez continuer.";
        }

        if (
          generationStatus
        ) {
          generationStatus.textContent =
            "PRÉPARATION TERMINÉE";

          generationStatus
            .closest(
              ".beryt-bilan__generation-status"
            )
            ?.classList.add(
              "is-complete"
            );
        }

        if (
          generationTime
        ) {
          generationTime.textContent =
            "PRÊT";
        }

        if (
          generationLine
        ) {
          generationLine.style.width =
            "100%";
        }

        if (
          generationNextButton
        ) {
          generationNextButton.disabled =
            false;
        }

        /*
         * Si la page est rechargée
         * maintenant, on reprendra
         * directement à l'e-mail.
         */

        draftPhase =
          "email";

        currentQuestionName =
          null;

        saveBilanDraft();
      };

    const startGenerationSequence = (
      nextGenerationMode =
        "fresh"
    ) => {
      if (
        generationTimer
      ) {
        window.clearInterval(
          generationTimer
        );
      }

            if (
        generationCountdownTimer
      ) {
        window.clearInterval(
          generationCountdownTimer
        );

        generationCountdownTimer =
          null;
      }

      generationStartedAt =
        Date.now();

      updateGenerationCountdown();

      generationCountdownTimer =
        window.setInterval(
          updateGenerationCountdown,
          250
        );

      generationAnalyticsMode =
        nextGenerationMode;

      trackBilanEvent(
        "generation_started",
        {
          generation_mode:
            generationAnalyticsMode
        }
      );

      if (
        generationNextButton
      ) {
        generationNextButton.disabled =
          true;
      }

      generationStatus
        ?.closest(
          ".beryt-bilan__generation-status"
        )
        ?.classList.remove(
          "is-complete"
        );

      generationSteps.forEach(
        (step) => {
          step.classList.remove(
            "is-active",
            "is-complete"
          );
        }
      );

      generationStageIndex =
        0;

      updateGenerationStage(
        0
      );

      draftPhase =
        "generation";

      currentQuestionName =
        null;

      saveBilanDraft();

      showScreen(
        "generation"
      );

      setProgress(
        98
      );

      scrollToBilanTop();

/*
 * Environ 5 secondes au total.
 * L'objectif est de laisser le temps
 * de lire les différentes étapes,
 * pas de simuler une vitesse de calcul.
 */

      generationTimer =
        window.setInterval(
          () => {
            if (
              generationStageIndex >=
              generationSteps.length -
                1
            ) {
              trackBilanEvent(
                "generation_completed",
                {
                  generation_mode:
                    generationAnalyticsMode
                }
              );

              completeGenerationSequence();
              return;
            }

            updateGenerationStage(
              generationStageIndex +
                1
            );
          },
          GENERATION_STEP_INTERVAL_MS
        );
    };
    const submitBilan = async (
      triggerButton
    ) => {
      if (isSubmitting) {
        return;
      }

      isSubmitting = true;

      let submitStage =
        !completedResultId ||
        !completedAccessToken
          ? "complete"
          : "result";

      trackBilanEvent(
        "submit_started"
      );

      if (
        submitError
      ) {
        submitError.hidden =
          true;
      }

      if (
        submitErrorText
      ) {
        submitErrorText.textContent =
          "";
      }

      if (triggerButton) {
        triggerButton.disabled =
          true;

        triggerButton.innerHTML =
          'Finalisation de votre Bilan <span aria-hidden="true">…</span>';
      }

      try {
        if (
          !completedResultId ||
          !completedAccessToken
        ) {
          const response = await fetch(
            "/apps/beryt-bilan/complete",
            {
              method: "POST",
              headers: {
                "content-type":
                  "application/json",
                accept:
                  "application/json"
              },
              credentials:
                "same-origin",
body: JSON.stringify({
answers:
  state.answers,

  lead: {
    first_name:
      state.lead.first_name,

    email:
      state.lead.email,

    bilan_data_consent:
      state.lead
        .bilan_data_consent,

    bilan_data_consent_version:
      state.lead
        .bilan_data_consent_version,

    bilan_data_consented_at:
      state.lead
        .bilan_data_consented_at,

    marketing_consent:
      state.lead
        .marketing_consent,

    marketing_consent_version:
      state.lead
        .marketing_consent_version
  }
})
            }
          );

          let data = null;

          try {
            data =
              await response.json();
          } catch {
            data = null;
          }

          if (
            !response.ok ||
            !data?.ok ||
            !data.result_id ||
            !data.access_token
          ) {
            const message =
              data?.error?.message ??
              `Impossible de créer le bilan (${response.status}).`;

            console.error(
              "BERYT Bilan — /complete refusé",
              {
                status:
                  response.status,
                error:
                  data?.error ?? null
              }
            );

            throw new Error(
              message
            );
          }

          completedResultId =
            data.result_id;

          completedAccessToken =
            data.access_token;

          completedResult =
            data.result ?? null;

          /*
           * Dès que /complete a réussi,
           * on conserve uniquement les
           * identifiants nécessaires pour
           * recharger CE résultat après
           * un refresh de l'onglet.
           *
           * Ni les réponses, ni l'e-mail,
           * ni le résultat complet ne sont
           * stockés dans sessionStorage.
           */

          saveBilanResultSession(
            completedResultId,
            completedAccessToken,
            state.lead.first_name
          );

          trackBilanEvent(
            "complete_succeeded"
          );

          if (
            window.__BERYT_BILAN_DEBUG__ ===
            true
          ) {
            console.log(
              "BERYT Bilan — /complete OK",
              {
                result_id:
                  completedResultId,

                access_token_received:
                  Boolean(
                    completedAccessToken
                  ),

                profile_code:
                  completedResult?.profile
                    ?.code ?? null,

                answers_exposed:
                  Boolean(
                    completedResult &&
                    Object.prototype.hasOwnProperty.call(
                      completedResult,
                      "answers"
                    )
                  )
              }
            );
          }
        }

        if (triggerButton) {
          triggerButton.innerHTML =
            'Chargement de votre résultat <span aria-hidden="true">…</span>';
        }

        submitStage =
          "result";

        const result =
          await loadBilanResult();

        renderResultHero(
          result
        );

        trackBilanEvent(
          "result_viewed",
          {
            load_mode:
              "fresh"
          }
        );
      } catch (error) {
        trackBilanEvent(
          "error",
          {
            error_stage:
              submitStage,

            online:
              navigator.onLine
          }
        );

        console.error(
          "BERYT Bilan — erreur de finalisation",
          error
        );

        if (
          submitErrorText
        ) {
          submitErrorText.textContent =
            navigator.onLine
              ? "Nous n’avons pas pu finaliser votre Bilan pour le moment. Vos réponses sont toujours là : vous pouvez réessayer."
              : "Votre connexion semble interrompue. Reconnectez-vous puis réessayez : vos réponses sont toujours là.";
        }

        if (
          submitError
        ) {
          submitError.hidden =
            false;
        }

        if (
          generationTimer
        ) {
          window.clearInterval(
            generationTimer
          );

          generationTimer =
            null;
        }

        showScreen(
          "email"
        );

        setProgress(
          99
        );

        if (triggerButton) {
          triggerButton.innerHTML =
            'Réessayer <span aria-hidden="true">→</span>';

          triggerButton.disabled =
            false;
        }
      } finally {
        isSubmitting = false;
      }
    };

    const hasMultipleMethods = () =>
      Array.isArray(
        state.answers.methods_used
      ) &&
      state.answers.methods_used.length > 1;

    const getReferenceMethod = () => {
      const methods =
        state.answers.methods_used;

      if (
        !Array.isArray(methods) ||
        methods.length === 0
      ) {
        return undefined;
      }

      if (methods.length === 1) {
        return methods[0];
      }

      return state.answers
        .method_for_primary_zone;
    };

    const getRegrowthFamily = () => {
      const referenceMethod =
        getReferenceMethod();

      if (!referenceMethod) {
        return undefined;
      }

      return methodFamilies[
        referenceMethod
      ];
    };

    const clearMethodForZoneAnswer = () => {
      methodForZoneInputs.forEach((input) => {
        input.checked = false;
      });

      delete state.answers
        .method_for_primary_zone;
    };

    const clearFrequencyAnswer = () => {
      frequencyInputs.forEach((input) => {
        input.checked = false;
      });

      delete state.answers.frequency;

      syncFrequencyQuickRead();
    };

    const clearRegrowthAnswer = () => {
      regrowthInputs.forEach((input) => {
        input.checked = false;
      });

      delete state.answers
        .regrowth_timing;

      syncRegrowthQuickRead();
    };

    const clearDensityAnswer = () => {
      densityInputs.forEach((input) => {
        input.checked = false;
      });

      delete state.answers
        .density_declared;
    };

    const clearThicknessAnswer = () => {
      thicknessInputs.forEach((input) => {
        input.checked = false;
      });

      delete state.answers
        .thickness_declared;
    };

    const syncPrimaryZoneLabel = () => {
      const selectedZone =
        state.answers.zone_primary;

      const label =
        selectedZone
          ? zoneLabels[selectedZone] ?? ""
          : "";

      primaryZoneLabels.forEach(
        (element) => {
          element.textContent = label;
        }
      );

      zoneContextBlocks.forEach(
        (block) => {
          const value =
            block.querySelector(
              "[data-bilan-question-zone-label]"
            );

          if (value) {
            value.textContent = label;
          }

          block.hidden = !label;
        }
      );
    };

    const updateMethodsNextState = () => {
      if (!methodsNextButton) {
        return;
      }

      methodsNextButton.disabled =
        !Array.isArray(
          state.answers.methods_used
        ) ||
        state.answers.methods_used.length === 0;
    };

    const updateZonesNextState = () => {
      if (!zonesNextButton) {
        return;
      }

      const hasZones =
        Array.isArray(state.answers.zones) &&
        state.answers.zones.length > 0;

      const hasPrimaryZone =
        Boolean(
          state.answers.zone_primary
        );

      zonesNextButton.disabled =
        !hasZones ||
        !hasPrimaryZone;
    };

    const updateMethodForZoneNextState =
      () => {
        if (!methodForZoneNextButton) {
          return;
        }

        methodForZoneNextButton.disabled =
          !Boolean(
            state.answers
              .method_for_primary_zone
          );
      };

    const updateFrequencyNextState = () => {
      if (!frequencyNextButton) {
        return;
      }

      frequencyNextButton.disabled =
        !Boolean(state.answers.frequency);
    };

    const updateRegrowthNextState = () => {
      if (!regrowthNextButton) {
        return;
      }

      regrowthNextButton.disabled =
        !Boolean(
          state.answers.regrowth_timing
        );
    };

    const updateDensityNextState = () => {
      if (!densityNextButton) {
        return;
      }

      densityNextButton.disabled =
        !Boolean(
          state.answers.density_declared
        );
    };

    const updateDensityAnswer = () => {
      const selectedDensity =
        densityInputs.find(
          (input) => input.checked
        );

      if (!selectedDensity) {
        delete state.answers
          .density_declared;

        updateDensityNextState();
        return;
      }

      state.answers.density_declared =
        selectedDensity.value;

      updateDensityNextState();
    };

    const updateThicknessNextState = () => {
      if (!thicknessNextButton) {
        return;
      }

      thicknessNextButton.disabled =
        !Boolean(
          state.answers.thickness_declared
        );
    };

    const updateThicknessAnswer = () => {
      const selectedThickness =
        thicknessInputs.find(
          (input) => input.checked
        );

      if (!selectedThickness) {
        delete state.answers
          .thickness_declared;

        updateThicknessNextState();
        return;
      }

      state.answers.thickness_declared =
        selectedThickness.value;

      updateThicknessNextState();
    };

    const updatePostEpilationSignsNextState = () => {
      if (!postEpilationSignsNextButton) {
        return;
      }

      postEpilationSignsNextButton.disabled =
        !Array.isArray(
          state.answers.post_epilation_signs
        ) ||
        state.answers.post_epilation_signs.length === 0;
    };

    const updateReactionDurationNextState = () => {
      if (!reactionDurationNextButton) {
        return;
      }

      reactionDurationNextButton.disabled =
        !Boolean(
          state.answers.reaction_duration
        );
    };

    const clearReactionDurationAnswer = () => {
      reactionDurationInputs.forEach(
        (input) => {
          input.checked = false;
        }
      );

      delete state.answers.reaction_duration;

      updateReactionDurationNextState();
      syncReactionDurationQuickRead();
    };

    const shouldAskReactionDuration = () =>
      Array.isArray(
        state.answers.post_epilation_signs
      ) &&
      state.answers.post_epilation_signs.length > 0 &&
      !state.answers.post_epilation_signs.includes(
        "nothing"
      );

    const updateReactionDurationAnswer = () => {
      const selectedDuration =
        reactionDurationInputs.find(
          (input) => input.checked
        );

      if (!selectedDuration) {
        delete state.answers.reaction_duration;
        updateReactionDurationNextState();
        syncReactionDurationQuickRead();
        return;
      }

      state.answers.reaction_duration =
        selectedDuration.value;

      updateReactionDurationNextState();
      syncReactionDurationQuickRead();
    };

    const updateIngrownFrequencyNextState = () => {
      if (!ingrownFrequencyNextButton) {
        return;
      }

      ingrownFrequencyNextButton.disabled =
        !Boolean(
          state.answers.ingrown_frequency
        );
    };

    const updateIngrownManifestationsNextState = () => {
      if (!ingrownManifestationsNextButton) {
        return;
      }

      ingrownManifestationsNextButton.disabled =
        !Array.isArray(
          state.answers.ingrown_manifestations
        ) ||
        state.answers.ingrown_manifestations.length === 0;
    };

    const clearIngrownManifestationsAnswer = () => {
      ingrownManifestationInputs.forEach(
        (input) => {
          input.checked = false;
        }
      );

      delete state.answers.ingrown_manifestations;

      updateIngrownManifestationsNextState();
    };

    const shouldAskIngrownManifestations = () =>
      [
        "occasionally",
        "regularly",
        "very_frequently"
      ].includes(
        state.answers.ingrown_frequency
      );

    const updateIngrownFrequencyAnswer = () => {
      const selectedFrequency =
        ingrownFrequencyInputs.find(
          (input) => input.checked
        );

      if (!selectedFrequency) {
        delete state.answers.ingrown_frequency;
        clearIngrownManifestationsAnswer();
        updateIngrownFrequencyNextState();
        return;
      }

      state.answers.ingrown_frequency =
        selectedFrequency.value;

      if (!shouldAskIngrownManifestations()) {
        clearIngrownManifestationsAnswer();
      }

      updateIngrownFrequencyNextState();
    };

    const updateIngrownManifestationsAnswer = () => {
      if (!shouldAskIngrownManifestations()) {
        clearIngrownManifestationsAnswer();
        return;
      }

      const selectedManifestations =
        getCheckedValues(
          ingrownManifestationInputs
        );

      if (selectedManifestations.length === 0) {
        delete state.answers
          .ingrown_manifestations;

        updateIngrownManifestationsNextState();
        return;
      }

      state.answers.ingrown_manifestations =
        selectedManifestations;

      updateIngrownManifestationsNextState();
    };

    const updateComfortPerceptionNextState = () => {
      if (!comfortPerceptionNextButton) {
        return;
      }

      comfortPerceptionNextButton.disabled =
        !Boolean(
          state.answers.comfort_perception
        );
    };

    const updateComfortPerceptionAnswer = () => {
      const selectedComfort =
        comfortPerceptionInputs.find(
          (input) => input.checked
        );

      if (!selectedComfort) {
        delete state.answers.comfort_perception;
        updateComfortPerceptionNextState();
        return;
      }

      state.answers.comfort_perception =
        selectedComfort.value;

      updateComfortPerceptionNextState();
    };
    const updateRoutineNextState = () => {
      if (!routineNextButton) {
        return;
      }

      routineNextButton.disabled =
        !Array.isArray(
          state.answers.routine
        ) ||
        state.answers.routine.length === 0;
    };

    const updateExfoliationTimingNextState = () => {
      if (!exfoliationTimingNextButton) {
        return;
      }

      exfoliationTimingNextButton.disabled =
        !Boolean(
          state.answers.exfoliation_timing
        );
    };

    const shouldAskExfoliationTiming = () =>
      Array.isArray(
        state.answers.routine
      ) &&
      state.answers.routine.some(
        (item) =>
          [
            "mechanical_exfoliant",
            "chemical_exfoliant",
            "ingrown_treatment"
          ].includes(item)
      );

    const clearExfoliationTimingAnswer = () => {
      exfoliationTimingInputs.forEach(
        (input) => {
          input.checked = false;
        }
      );

      delete state.answers.exfoliation_timing;

      updateExfoliationTimingNextState();
    };

    const updateExfoliationTimingAnswer = () => {
      if (!shouldAskExfoliationTiming()) {
        clearExfoliationTimingAnswer();
        return;
      }

      const selectedTiming =
        exfoliationTimingInputs.find(
          (input) => input.checked
        );

      if (!selectedTiming) {
        delete state.answers.exfoliation_timing;
        updateExfoliationTimingNextState();
        return;
      }

      state.answers.exfoliation_timing =
        selectedTiming.value;

      updateExfoliationTimingNextState();
    };

    let draggedPriorityValue =
      null;

    const getPriorityDisplayLabel = (
      value
    ) => {
      const input =
        priorityInputs.find(
          (item) =>
            item.value === value
        );

      const label =
        input
          ?.closest(
            ".beryt-bilan__choice"
          )
          ?.querySelector(
            ".beryt-bilan__choice-name"
          )
          ?.textContent
          ?.trim();

      return label || value;
    };

    const updatePriorityNextState = () => {
      if (!priorityNextButton) {
        return;
      }

      priorityNextButton.disabled =
        !Boolean(
          state.answers.priority
        );
    };

    const syncPriorityInputAvailability =
      () => {
        const ranking =
          Array.isArray(
            state.answers
              .priority_ranking
          )
            ? state.answers
                .priority_ranking
            : [];

        const hasReachedLimit =
          ranking.length >= 3;

        priorityInputs.forEach(
          (input) => {
            input.disabled =
              hasReachedLimit &&
              !input.checked;
          }
        );

        if (
          prioritySelectionStatus
        ) {
          prioritySelectionStatus.textContent =
            `${ranking.length} / 3 ${
              ranking.length === 1
                ? "sélectionné"
                : "sélectionnés"
            }`;
        }
      };

    const setPriorityRanking = (
      nextRanking
    ) => {
      const ranking =
        nextRanking.slice(
          0,
          3
        );

      if (ranking.length === 0) {
        delete state.answers
          .priority_ranking;

        delete state.answers
          .priority;
      } else {
        state.answers
          .priority_ranking =
          ranking;

        state.answers.priority =
          ranking[0];
      }

      renderPriorityRanking();
      syncPriorityInputAvailability();
      updatePriorityNextState();

      saveBilanDraft();
    };

    const movePriority = (
      value,
      direction
    ) => {
      const ranking =
        Array.isArray(
          state.answers
            .priority_ranking
        )
          ? [
              ...state.answers
                .priority_ranking
            ]
          : [];

      const currentIndex =
        ranking.indexOf(
          value
        );

      const nextIndex =
        currentIndex +
        direction;

      if (
        currentIndex < 0 ||
        nextIndex < 0 ||
        nextIndex >=
          ranking.length
      ) {
        return;
      }

      const [
        movedItem
      ] =
        ranking.splice(
          currentIndex,
          1
        );

      ranking.splice(
        nextIndex,
        0,
        movedItem
      );

      setPriorityRanking(
        ranking
      );
    };

    const reorderPriority = (
      sourceValue,
      targetValue
    ) => {
      if (
        !sourceValue ||
        !targetValue ||
        sourceValue ===
          targetValue
      ) {
        return;
      }

      const ranking =
        Array.isArray(
          state.answers
            .priority_ranking
        )
          ? [
              ...state.answers
                .priority_ranking
            ]
          : [];

      const sourceIndex =
        ranking.indexOf(
          sourceValue
        );

      const targetIndex =
        ranking.indexOf(
          targetValue
        );

      if (
        sourceIndex < 0 ||
        targetIndex < 0
      ) {
        return;
      }

      const [
        movedItem
      ] =
        ranking.splice(
          sourceIndex,
          1
        );

      ranking.splice(
        targetIndex,
        0,
        movedItem
      );

      setPriorityRanking(
        ranking
      );
    };

    const renderPriorityRanking =
      () => {
        const ranking =
          Array.isArray(
            state.answers
              .priority_ranking
          )
            ? state.answers
                .priority_ranking
            : [];

        if (
          priorityRankingBlock
        ) {
          priorityRankingBlock.hidden =
            ranking.length < 1;

          const rankingHelp =
            priorityRankingBlock.querySelector(
              "[data-bilan-priority-ranking-help]"
            );

          const rankingNote =
            priorityRankingBlock.querySelector(
              "[data-bilan-priority-ranking-note]"
            );

          if (rankingHelp) {
            rankingHelp.textContent =
              ranking.length === 1
                ? "Votre premier choix est votre priorité principale."
                : "Vos objectifs sont classés dans l’ordre de vos choix. Utilisez les flèches pour modifier cet ordre.";
          }

          if (rankingNote) {
            rankingNote.textContent =
              ranking.length === 1
                ? "Vous pouvez encore ajouter jusqu’à 2 objectifs."
                : ranking.length === 2
                  ? "Vous pouvez encore ajouter 1 objectif."
                  : "Vos 3 priorités sont sélectionnées.";
          }
        }

        if (
          !priorityRankingList
        ) {
          return;
        }

        priorityRankingList.replaceChildren();

        ranking.forEach(
          (
            value,
            index
          ) => {
            const row =
              document.createElement(
                "div"
              );

            row.className =
              "beryt-bilan__priority-rank-row";

            if (index === 0) {
              row.classList.add(
                "is-primary"
              );
            }

            row.dataset
              .priorityValue =
              value;

            row.setAttribute(
              "role",
              "listitem"
            );

            row.draggable =
              false;

            const number =
              document.createElement(
                "span"
              );

            number.className =
              "beryt-bilan__priority-rank-number";

            number.textContent =
              String(
                index + 1
              ).padStart(
                2,
                "0"
              );

            const title =
              document.createElement(
                "span"
              );

            title.className =
              "beryt-bilan__priority-rank-title";

            title.textContent =
              getPriorityDisplayLabel(
                value
              );

            const controls =
              document.createElement(
                "div"
              );

            controls.className =
              "beryt-bilan__priority-rank-controls";

            const upButton =
              document.createElement(
                "button"
              );

            upButton.type =
              "button";

            upButton.className =
              "beryt-bilan__priority-rank-button";

            upButton.textContent =
              "↑";

            upButton.disabled =
              index === 0;

            upButton.setAttribute(
              "aria-label",
              `Monter ${getPriorityDisplayLabel(
                value
              )} dans vos priorités`
            );

            upButton.addEventListener(
              "click",
              () => {
                movePriority(
                  value,
                  -1
                );
              }
            );

            const downButton =
              document.createElement(
                "button"
              );

            downButton.type =
              "button";

            downButton.className =
              "beryt-bilan__priority-rank-button";

            downButton.textContent =
              "↓";

            downButton.disabled =
              index ===
              ranking.length - 1;

            downButton.setAttribute(
              "aria-label",
              `Descendre ${getPriorityDisplayLabel(
                value
              )} dans vos priorités`
            );

            downButton.addEventListener(
              "click",
              () => {
                movePriority(
                  value,
                  1
                );
              }
            );

            controls.append(
              upButton,
              downButton
            );

            const priorityTag =
              document.createElement(
                "span"
              );

            priorityTag.className =
              "beryt-bilan__priority-rank-tag";

            priorityTag.textContent =
              index === 0
                ? "PRIORITÉ PRINCIPALE"
                : `PRIORITÉ ${index + 1}`;

            const content =
              document.createElement(
                "div"
              );

            content.className =
              "beryt-bilan__priority-rank-content";

            content.append(
              priorityTag,
              title
            );

            row.append(
              number,
              content,
              controls
            );

            row.addEventListener(
              "dragstart",
              (event) => {
                draggedPriorityValue =
                  value;

                row.classList.add(
                  "is-dragging"
                );

                if (
                  event.dataTransfer
                ) {
                  event.dataTransfer.effectAllowed =
                    "move";

                  event.dataTransfer.setData(
                    "text/plain",
                    value
                  );
                }
              }
            );

            row.addEventListener(
              "dragend",
              () => {
                draggedPriorityValue =
                  null;

                row.classList.remove(
                  "is-dragging"
                );
              }
            );

            row.addEventListener(
              "dragover",
              (event) => {
                event.preventDefault();

                row.classList.add(
                  "is-drop-target"
                );

                if (
                  event.dataTransfer
                ) {
                  event.dataTransfer.dropEffect =
                    "move";
                }
              }
            );

            row.addEventListener(
              "dragleave",
              () => {
                row.classList.remove(
                  "is-drop-target"
                );
              }
            );

            row.addEventListener(
              "drop",
              (event) => {
                event.preventDefault();

                row.classList.remove(
                  "is-drop-target"
                );

                const sourceValue =
                  draggedPriorityValue ??
                  event.dataTransfer
                    ?.getData(
                      "text/plain"
                    );

                reorderPriority(
                  sourceValue,
                  value
                );
              }
            );

            priorityRankingList.appendChild(
              row
            );
          }
        );
      };

    const updatePriorityAnswer = (
      changedInput
    ) => {
      let ranking =
        Array.isArray(
          state.answers
            .priority_ranking
        )
          ? [
              ...state.answers
                .priority_ranking
            ]
          : priorityInputs
              .filter(
                (input) =>
                  input.checked
              )
              .map(
                (input) =>
                  input.value
              );

      if (changedInput) {
        if (
          changedInput.checked
        ) {
          if (
            !ranking.includes(
              changedInput.value
            )
          ) {
            if (
              ranking.length >=
              3
            ) {
              changedInput.checked =
                false;
            } else {
              ranking.push(
                changedInput.value
              );
            }
          }
        } else {
          ranking =
            ranking.filter(
              (value) =>
                value !==
                changedInput.value
            );
        }
      }

      priorityInputs.forEach(
        (input) => {
          input.checked =
            ranking.includes(
              input.value
            );
        }
      );

      setPriorityRanking(
        ranking
      );
    };

    const shouldAskSafetyGate = () => {
      return true;
    };

    const updateSafetyAnswersNextState = () => {
      if (!safetyAnswersNextButton) {
        return;
      }

      safetyAnswersNextButton.disabled =
        !Array.isArray(
          state.answers.safety_answers
        ) ||
        state.answers.safety_answers.length === 0;
    };

    const clearSafetyAnswers = () => {
      safetyAnswerInputs.forEach(
        (input) => {
          input.checked = false;
        }
      );

      delete state.answers.safety_answers;

      updateSafetyAnswersNextState();
    };

    const syncSafetyGateState = () => {
      if (!shouldAskSafetyGate()) {
        clearSafetyAnswers();
      }
    };

    const updateSafetyAnswers = (
      changedInput
    ) => {
      if (!shouldAskSafetyGate()) {
        clearSafetyAnswers();
        return;
      }

      if (
        changedInput?.value === "none" &&
        changedInput.checked
      ) {
        safetyAnswerInputs.forEach(
          (input) => {
            if (input.value !== "none") {
              input.checked = false;
            }
          }
        );
      }

      if (
        changedInput &&
        changedInput.value !== "none" &&
        changedInput.checked
      ) {
        const noneInput =
          safetyAnswerInputs.find(
            (input) =>
              input.value === "none"
          );

        if (noneInput) {
          noneInput.checked = false;
        }
      }

      const selectedSafetyAnswers =
        getCheckedValues(
          safetyAnswerInputs
        );

      if (
        selectedSafetyAnswers.length === 0
      ) {
        delete state.answers.safety_answers;
        updateSafetyAnswersNextState();
        return;
      }

      state.answers.safety_answers =
        selectedSafetyAnswers;

      updateSafetyAnswersNextState();
    };

    const updateRoutineAnswer = (
      changedInput
    ) => {
      if (
        changedInput?.value === "nothing" &&
        changedInput.checked
      ) {
        routineInputs.forEach(
          (input) => {
            if (input.value !== "nothing") {
              input.checked = false;
            }
          }
        );
      }

      if (
        changedInput &&
        changedInput.value !== "nothing" &&
        changedInput.checked
      ) {
        const nothingInput =
          routineInputs.find(
            (input) =>
              input.value === "nothing"
          );

        if (nothingInput) {
          nothingInput.checked = false;
        }
      }

      const selectedRoutine =
        getCheckedValues(
          routineInputs
        );

      if (selectedRoutine.length === 0) {
        delete state.answers.routine;

        clearExfoliationTimingAnswer();
        updateRoutineNextState();
        return;
      }

      state.answers.routine =
        selectedRoutine;

      if (!shouldAskExfoliationTiming()) {
        clearExfoliationTimingAnswer();
      }

      updateRoutineNextState();
    };

    const updatePostEpilationSignsAnswer = (
      changedInput
    ) => {
      if (
        changedInput?.value === "nothing" &&
        changedInput.checked
      ) {
        postEpilationSignInputs.forEach(
          (input) => {
            if (input.value !== "nothing") {
              input.checked = false;
            }
          }
        );
      }

      if (
        changedInput &&
        changedInput.value !== "nothing" &&
        changedInput.checked
      ) {
        const nothingInput =
          postEpilationSignInputs.find(
            (input) =>
              input.value === "nothing"
          );

        if (nothingInput) {
          nothingInput.checked = false;
        }
      }

      const selectedSigns =
        getCheckedValues(
          postEpilationSignInputs
        );

      if (selectedSigns.length === 0) {
        delete state.answers
          .post_epilation_signs;

        clearReactionDurationAnswer();
        updatePostEpilationSignsNextState();
        syncPostEpilationQuickRead();
        return;
      }

      state.answers.post_epilation_signs =
        selectedSigns;

      if (!shouldAskReactionDuration()) {
        clearReactionDurationAnswer();
      }

      updatePostEpilationSignsNextState();
      syncPostEpilationQuickRead();
    };

    const syncMethodForZoneOptions = () => {
      const selectedMethods =
        Array.isArray(
          state.answers.methods_used
        )
          ? state.answers.methods_used
          : [];

      methodForZoneOptions.forEach(
        (option) => {
          const method =
            option.dataset
              .methodForZoneOption;

          option.hidden =
            !method ||
            !selectedMethods.includes(
              method
            );
        }
      );

      const currentMethod =
        state.answers
          .method_for_primary_zone;

      if (
        currentMethod &&
        !selectedMethods.includes(
          currentMethod
        )
      ) {
        clearMethodForZoneAnswer();
      }

      updateMethodForZoneNextState();
    };

    const syncRegrowthQuestion = () => {
      const family =
        getRegrowthFamily();

      regrowthOptions.forEach((option) => {
        const optionFamily =
          option.dataset.regrowthFamily;

        option.hidden =
          optionFamily !== "all" &&
          optionFamily !== family;
      });

      if (family === "light") {
        if (regrowthTitle) {
          regrowthTitle.textContent =
            "Aujourd’hui, comment décririez-vous la repousse ?";
        }

        if (regrowthHelp) {
          regrowthHelp.textContent =
            "Répondez selon votre situation actuelle, même si votre protocole IPL ou laser est encore en cours.";
        }
      } else {
        if (regrowthTitle) {
          regrowthTitle.textContent =
            "Au bout de combien de temps la repousse devient-elle généralement visible ?";
        }

        if (regrowthHelp) {
          regrowthHelp.textContent =
            "Pensez au moment où les premiers poils redeviennent clairement visibles.";
        }
      }

      const selectedRegrowth =
        regrowthInputs.find(
          (input) => input.checked
        );

      if (selectedRegrowth) {
        const selectedOption =
          selectedRegrowth.closest(
            "[data-regrowth-family]"
          );

        const selectedFamily =
          selectedOption?.dataset
            .regrowthFamily;

        const isCompatible =
          selectedFamily === "all" ||
          selectedFamily === family;

        if (!isCompatible) {
          clearRegrowthAnswer();
        }
      }

      updateRegrowthNextState();
      syncRegrowthQuickRead();
    };

    const updateMethodsAnswer = () => {
      const previousReferenceMethod =
        getReferenceMethod();

      const selectedMethods =
        getCheckedValues(methodInputs);

      if (selectedMethods.length === 0) {
        delete state.answers.methods_used;
        clearMethodForZoneAnswer();
        clearRegrowthAnswer();
        syncMethodForZoneOptions();
        syncRegrowthQuestion();
        updateMethodsNextState();
        return;
      }

      state.answers.methods_used =
        selectedMethods;

      if (selectedMethods.length === 1) {
        clearMethodForZoneAnswer();
      } else {
        const currentMethodForZone =
          state.answers
            .method_for_primary_zone;

        if (
          currentMethodForZone &&
          !selectedMethods.includes(
            currentMethodForZone
          )
        ) {
          clearMethodForZoneAnswer();
        }
      }

      const currentReferenceMethod =
        getReferenceMethod();

      if (
        previousReferenceMethod !==
        currentReferenceMethod
      ) {
        clearRegrowthAnswer();
      }

      syncMethodForZoneOptions();
      syncRegrowthQuestion();
      updateMethodsNextState();
    };

    const updateMethodForZoneAnswer =
      () => {
        const previousReferenceMethod =
          getReferenceMethod();

        if (!hasMultipleMethods()) {
          clearMethodForZoneAnswer();

          if (
            previousReferenceMethod !==
            getReferenceMethod()
          ) {
            clearRegrowthAnswer();
          }

          syncRegrowthQuestion();
          updateMethodForZoneNextState();
          return;
        }

        const selectedMethod =
          methodForZoneInputs.find(
            (input) => input.checked
          );

        if (!selectedMethod) {
          delete state.answers
            .method_for_primary_zone;

          if (previousReferenceMethod) {
            clearRegrowthAnswer();
          }

          syncRegrowthQuestion();
          updateMethodForZoneNextState();
          return;
        }

        const selectedMethods =
          state.answers.methods_used ?? [];

        if (
          !selectedMethods.includes(
            selectedMethod.value
          )
        ) {
          selectedMethod.checked = false;

          delete state.answers
            .method_for_primary_zone;

          clearRegrowthAnswer();
          syncRegrowthQuestion();
          updateMethodForZoneNextState();
          return;
        }

        state.answers
          .method_for_primary_zone =
          selectedMethod.value;

        if (
          previousReferenceMethod !==
          selectedMethod.value
        ) {
          clearRegrowthAnswer();
        }

        syncRegrowthQuestion();
        updateMethodForZoneNextState();
      };

    const updateFrequencyAnswer = () => {
      const selectedFrequency =
        frequencyInputs.find(
          (input) => input.checked
        );

      if (!selectedFrequency) {
        delete state.answers.frequency;
        updateFrequencyNextState();
        syncFrequencyQuickRead();
        return;
      }

      state.answers.frequency =
        selectedFrequency.value;

      updateFrequencyNextState();
      syncFrequencyQuickRead();
    };

    const updateRegrowthAnswer = () => {
      const selectedRegrowth =
        regrowthInputs.find(
          (input) => input.checked
        );

      if (!selectedRegrowth) {
        delete state.answers
          .regrowth_timing;

        updateRegrowthNextState();
        syncRegrowthQuickRead();
        return;
      }

      state.answers.regrowth_timing =
        selectedRegrowth.value;

      updateRegrowthNextState();
      syncRegrowthQuickRead();
    };

    const updatePrimaryZoneAnswer = () => {
      const selectedPrimaryZone =
        primaryZoneInputs.find(
          (input) => input.checked
        );

      const previousPrimaryZone =
        state.answers.zone_primary;

      if (!selectedPrimaryZone) {
        delete state.answers.zone_primary;

        if (previousPrimaryZone) {
          clearMethodForZoneAnswer();
          clearFrequencyAnswer();
          clearRegrowthAnswer();
          clearDensityAnswer();
          clearThicknessAnswer();
        }

        syncPrimaryZoneLabel();
        syncRegrowthQuestion();
        updateZonesNextState();
        updateFrequencyNextState();
        return;
      }

      state.answers.zone_primary =
        selectedPrimaryZone.value;

      if (
        previousPrimaryZone &&
        previousPrimaryZone !==
          selectedPrimaryZone.value
      ) {
        clearMethodForZoneAnswer();
        clearFrequencyAnswer();
        clearRegrowthAnswer();
      }

      syncPrimaryZoneLabel();
      syncRegrowthQuestion();
      updateZonesNextState();
      updateFrequencyNextState();
    };

    const updateZonesAnswer = () => {
      const selectedZones =
        getCheckedValues(zoneInputs);

      const previousPrimaryZone =
        state.answers.zone_primary;

      if (selectedZones.length === 0) {
        delete state.answers.zones;
        delete state.answers.zone_primary;

        primaryZoneInputs.forEach(
          (input) => {
            input.checked = false;
          }
        );

        primaryZoneOptions.forEach(
          (option) => {
            option.hidden = true;
          }
        );

        if (primaryZoneBlock) {
          primaryZoneBlock.hidden = true;
        }

        clearMethodForZoneAnswer();
        clearFrequencyAnswer();
        clearRegrowthAnswer();
        syncPrimaryZoneLabel();
        syncRegrowthQuestion();
        updateZonesNextState();
        updateFrequencyNextState();
        return;
      }

      state.answers.zones =
        selectedZones;

      primaryZoneOptions.forEach(
        (option) => {
          const zone =
            option.dataset.primaryZoneOption;

          option.hidden =
            !zone ||
            !selectedZones.includes(zone);
        }
      );

      if (selectedZones.length === 1) {
        const onlyZone =
          selectedZones[0];

        primaryZoneInputs.forEach(
          (input) => {
            input.checked =
              input.value === onlyZone;
          }
        );

        state.answers.zone_primary =
          onlyZone;

        if (
          previousPrimaryZone &&
          previousPrimaryZone !== onlyZone
        ) {
          clearMethodForZoneAnswer();
          clearFrequencyAnswer();
          clearRegrowthAnswer();
        }

        if (primaryZoneBlock) {
          primaryZoneBlock.hidden = true;
        }

        syncPrimaryZoneLabel();
        syncRegrowthQuestion();
        updateZonesNextState();
        updateFrequencyNextState();
        return;
      }

      if (primaryZoneBlock) {
        primaryZoneBlock.hidden = false;
      }

      const currentPrimaryInput =
        primaryZoneInputs.find(
          (input) => input.checked
        );

      if (
        currentPrimaryInput &&
        selectedZones.includes(
          currentPrimaryInput.value
        )
      ) {
        state.answers.zone_primary =
          currentPrimaryInput.value;
      } else {
        primaryZoneInputs.forEach(
          (input) => {
            input.checked = false;
          }
        );

        delete state.answers.zone_primary;

        if (previousPrimaryZone) {
          clearMethodForZoneAnswer();
          clearFrequencyAnswer();
          clearRegrowthAnswer();
        }
      }

      syncPrimaryZoneLabel();
      syncRegrowthQuestion();
      updateZonesNextState();
      updateFrequencyNextState();
    };

    const syncFirstName = () => {
      const rawFirstName =
        firstNameInput?.value
          .trim() ?? "";

      const lowerFirstName =
        rawFirstName.toLocaleLowerCase(
          "fr-FR"
        );

      let firstName = "";
      let capitalizeNext = true;

      for (
        const character of
        lowerFirstName
      ) {
        const isSeparator =
          character === " " ||
          character === "-" ||
          character === "'";

        if (capitalizeNext) {
          firstName +=
            character.toLocaleUpperCase(
              "fr-FR"
            );

          capitalizeNext = false;
        } else {
          firstName +=
            character;
        }

        if (isSeparator) {
          capitalizeNext = true;
        }
      }

      state.lead.first_name =
        firstName;

      if (firstNameNextButton) {
        firstNameNextButton.disabled =
          firstName.length === 0;
      }

      firstNameDisplays.forEach(
        (element) => {
          element.textContent =
            firstName;
        }
      );
    };

    const syncDataConsent = () => {
      const isChecked =
        Boolean(
          dataConsentInput?.checked
        );

      state.lead.bilan_data_consent =
        isChecked;

      if (!isChecked) {
        state.lead
          .bilan_data_consent_version =
          null;

        state.lead
          .bilan_data_consented_at =
          null;
      }

      if (consentNextButton) {
        consentNextButton.disabled =
          !isChecked;
      }
    };

const showIdentityScreen = () => {
  showScreen("identity");

  setProgress(2);

  scrollToBilanTop();
};

    const showConsentScreen = () => {
      syncFirstName();

      if (!state.lead.first_name) {
        return;
      }

      firstNameDisplays.forEach(
        (element) => {
          element.textContent =
            state.lead.first_name;
        }
      );

      showScreen("consent");

      setProgress(4);

      scrollToBilanTop();
    };

    const beginQuestionnaire = () => {
      syncDataConsent();

      if (
        !state.lead
          .bilan_data_consent
      ) {
        return;
      }

      state.lead
        .bilan_data_consent_version =
        BILAN_DATA_CONSENT_VERSION;

      state.lead
        .bilan_data_consented_at =
        new Date().toISOString();

      showScreen("questionnaire");

      showQuestion(
        "methods_used",
        8
      );
    };

    const isValidEmail = (
      value
    ) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        value
      );

    const syncEmailGate = () => {
      const email =
        emailInput?.value
          .trim() ?? "";

      state.lead.email =
        email;

      state.lead.marketing_consent =
        Boolean(
          marketingConsentInput?.checked
        );

      state.lead.marketing_consent_version =
        MARKETING_CONSENT_VERSION;

      if (emailSubmitButton) {
        emailSubmitButton.disabled =
          !isValidEmail(email);
      }
    };

    const showEmailGate = () => {
      firstNameDisplays.forEach(
        (element) => {
          element.textContent =
            state.lead.first_name;
        }
      );

      syncEmailGate();

      trackBilanEvent(
        "email_gate_viewed"
      );

      draftPhase =
        "email";

      currentQuestionName =
        null;

      showScreen("email");

      setProgress(99);

      saveBilanDraft();

      scrollToBilanTop();
    };

    const returnFromEmailGate = () => {
      /*
       * L'écran e-mail n'est accessible
       * qu'une fois la préparation terminée.
       *
       * Après un refresh, le DOM de l'écran
       * de préparation repart visuellement
       * de son état initial. On le remet donc
       * explicitement dans son état terminé
       * avant de l'afficher.
       */

      completeGenerationSequence();

      showScreen(
        "generation"
      );

      setProgress(
        98
      );

      scrollToBilanTop();
    };


    /* =========================
       RESTAURATION DU BROUILLON
    ========================== */

    const applyDraftValueToInputs = (
      inputs,
      value
    ) => {
      const values =
        Array.isArray(
          value
        )
          ? value
          : value ===
              undefined ||
            value ===
              null
            ? []
            : [
                value
              ];

      inputs.forEach(
        (input) => {
          input.checked =
            values.includes(
              input.value
            );
        }
      );
    };

    const restoreDraftState = (
      draft
    ) => {
      isRestoringDraft =
        true;

      try {
        state.answers =
          JSON.parse(
            JSON.stringify(
              draft.answers ??
                {}
            )
          );

        state.lead.first_name =
          typeof draft.first_name ===
            "string"
            ? draft.first_name
            : "";

        /*
         * L'e-mail et le consentement
         * marketing ne sont volontairement
         * jamais restaurés depuis localStorage.
         */

        state.lead.email =
          "";

        state.lead.marketing_consent =
          false;

        state.lead.marketing_consent_version =
          null;

        state.lead.bilan_data_consent =
          true;

        state.lead.bilan_data_consent_version =
          draft
            .bilan_data_consent_version ??
          BILAN_DATA_CONSENT_VERSION;

        state.lead.bilan_data_consented_at =
          draft
            .bilan_data_consented_at ??
          new Date()
            .toISOString();

        if (
          firstNameInput
        ) {
          firstNameInput.value =
            state.lead.first_name;
        }

        firstNameDisplays.forEach(
          (element) => {
            element.textContent =
              state.lead.first_name;
          }
        );

        if (
          dataConsentInput
        ) {
          dataConsentInput.checked =
            true;
        }

        if (
          emailInput
        ) {
          emailInput.value =
            "";
        }

        if (
          marketingConsentInput
        ) {
          marketingConsentInput.checked =
            false;
        }

        applyDraftValueToInputs(
          methodInputs,
          state.answers
            .methods_used
        );

        applyDraftValueToInputs(
          zoneInputs,
          state.answers.zones
        );

        applyDraftValueToInputs(
          primaryZoneInputs,
          state.answers
            .zone_primary
        );

        applyDraftValueToInputs(
          methodForZoneInputs,
          state.answers
            .method_for_primary_zone
        );

        applyDraftValueToInputs(
          frequencyInputs,
          state.answers.frequency
        );

        applyDraftValueToInputs(
          regrowthInputs,
          state.answers
            .regrowth_timing
        );

        applyDraftValueToInputs(
          densityInputs,
          state.answers
            .density_declared
        );

        applyDraftValueToInputs(
          thicknessInputs,
          state.answers
            .thickness_declared
        );

        applyDraftValueToInputs(
          postEpilationSignInputs,
          state.answers
            .post_epilation_signs
        );

        applyDraftValueToInputs(
          reactionDurationInputs,
          state.answers
            .reaction_duration
        );

        applyDraftValueToInputs(
          ingrownFrequencyInputs,
          state.answers
            .ingrown_frequency
        );

        applyDraftValueToInputs(
          ingrownManifestationInputs,
          state.answers
            .ingrown_manifestations
        );

        applyDraftValueToInputs(
          comfortPerceptionInputs,
          state.answers
            .comfort_perception
        );

        applyDraftValueToInputs(
          routineInputs,
          state.answers.routine
        );

        applyDraftValueToInputs(
          exfoliationTimingInputs,
          state.answers
            .exfoliation_timing
        );

        const restoredRanking =
          Array.isArray(
            state.answers
              .priority_ranking
          )
            ? state.answers
                .priority_ranking
            : state.answers
                .priority
              ? [
                  state.answers
                    .priority
                ]
              : [];

        applyDraftValueToInputs(
          priorityInputs,
          restoredRanking
        );

        applyDraftValueToInputs(
          safetyAnswerInputs,
          state.answers
            .safety_answers
        );


        /*
         * On repasse ensuite dans
         * la logique existante du Bilan.
         *
         * Cela nettoie automatiquement
         * une réponse conditionnelle devenue
         * incompatible.
         */

        syncFirstName();
        syncDataConsent();

        updateMethodsAnswer();
        updateZonesAnswer();
        syncMethodForZoneOptions();
        updateMethodForZoneAnswer();
        updateFrequencyAnswer();
        syncRegrowthQuestion();
        updateRegrowthAnswer();
        updateDensityAnswer();
        updateThicknessAnswer();
        updatePostEpilationSignsAnswer();
        updateReactionDurationAnswer();
        updateIngrownFrequencyAnswer();
        updateIngrownManifestationsAnswer();
        updateComfortPerceptionAnswer();
        updateRoutineAnswer();
        updateExfoliationTimingAnswer();
        updatePriorityAnswer();
        syncSafetyGateState();
        updateSafetyAnswersNextState();

        draftPhase =
          draft.phase ??
          "questionnaire";

        currentQuestionName =
          draft.current_question ??
          null;

        currentProgressValue =
          Number.isFinite(
            Number(
              draft.progress
            )
          )
            ? Number(
                draft.progress
              )
            : 8;
      } finally {
        isRestoringDraft =
          false;
      }
    };

    const getSafeResumeQuestion = (
      requestedQuestion
    ) => {
      let questionName =
        requestedQuestion;

      const questionExists =
        questions.some(
          (question) =>
            question.dataset
              .bilanQuestion ===
            questionName
        );

      if (
        !questionExists
      ) {
        return "methods_used";
      }

      if (
        questionName ===
          "method_for_primary_zone" &&
        !hasMultipleMethods()
      ) {
        return "frequency";
      }

      if (
        questionName ===
          "reaction_duration" &&
        !shouldAskReactionDuration()
      ) {
        return "ingrown_frequency";
      }

      if (
        questionName ===
          "ingrown_manifestations" &&
        !shouldAskIngrownManifestations()
      ) {
        return "comfort_perception";
      }

      if (
        questionName ===
          "exfoliation_timing" &&
        !shouldAskExfoliationTiming()
      ) {
        return "priority";
      }

      return questionName;
    };

    const showRecoveryScreen = (
      draft
    ) => {
      const progressValue =
        Number.isFinite(
          Number(
            draft.progress
          )
        )
          ? Math.max(
              0,
              Math.min(
                99,
                Number(
                  draft.progress
                )
              )
            )
          : 0;

      if (
        recoveryProgress
      ) {
        recoveryProgress.textContent =
          `${Math.round(
            progressValue
          )} % COMPLÉTÉ`;
      }

      if (
        recoveryMeta
      ) {
        recoveryMeta.textContent =
          `Dernière activité ${formatDraftAge(
            draft.saved_at
          )}. Ce brouillon est conservé uniquement sur cet appareil et sera supprimé automatiquement après 72 h.`;
      }

      setExperienceMode(
        true
      );

      showScreen(
        "recovery"
      );

      trackBilanEvent(
        "recovery_shown",
        {
          recovery_type:
            "draft"
        }
      );

      setProgress(
        progressValue
      );

      scrollToBilanTop();
    };

    const resumeDraft = (
      draft
    ) => {
      restoreDraftState(
        draft
      );

      setExperienceMode(
        true
      );

      if (
        draft.phase ===
        "email"
      ) {
        showEmailGate();
        return;
      }

      if (
        draft.phase ===
        "generation"
      ) {
        /*
         * Une préparation interrompue
         * repart volontairement du début.
         */

        startGenerationSequence(
          "recovered"
        );

        return;
      }

      const resumeQuestion =
        getSafeResumeQuestion(
          draft.current_question ??
            "methods_used"
        );

      const resumeProgress =
        Number.isFinite(
          Number(
            draft.progress
          )
        )
          ? Number(
              draft.progress
            )
          : 8;

      showScreen(
        "questionnaire"
      );

      showQuestion(
        resumeQuestion,
        resumeProgress
      );
    };


    const handleStart = () => {
      trackBilanEvent(
        "started",
        {
          entry_mode:
            "fresh"
        }
      );

      setExperienceMode(true);

      showIdentityScreen();
    };

    methodInputs.forEach((input) => {
      input.addEventListener(
        "change",
        updateMethodsAnswer
      );
    });

    zoneInputs.forEach((input) => {
      input.addEventListener(
        "change",
        updateZonesAnswer
      );
    });

    primaryZoneInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          updatePrimaryZoneAnswer
        );
      }
    );

    methodForZoneInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          updateMethodForZoneAnswer
        );
      }
    );

    frequencyInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          updateFrequencyAnswer
        );
      }
    );

    regrowthInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          updateRegrowthAnswer
        );
      }
    );

    densityInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          updateDensityAnswer
        );
      }
    );

    thicknessInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          updateThicknessAnswer
        );
      }
    );
    postEpilationSignInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          () => {
            updatePostEpilationSignsAnswer(
              input
            );

            syncSafetyGateState();
          }
        );
      }
    );

    reactionDurationInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          () => {
            updateReactionDurationAnswer();
            syncSafetyGateState();
          }
        );
      }
    );

    ingrownFrequencyInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          () => {
            updateIngrownFrequencyAnswer();
            syncSafetyGateState();
          }
        );
      }
    );

    ingrownManifestationInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          () => {
            updateIngrownManifestationsAnswer();
            syncSafetyGateState();
          }
        );
      }
    );

    comfortPerceptionInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          updateComfortPerceptionAnswer
        );
      }
    );

    routineInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          () => {
            updateRoutineAnswer(
              input
            );
          }
        );
      }
    );

    exfoliationTimingInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          updateExfoliationTimingAnswer
        );
      }
    );

    priorityInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          () => {
            updatePriorityAnswer(
              input
            );
          }
        );
      }
    );

    safetyAnswerInputs.forEach(
      (input) => {
        input.addEventListener(
          "change",
          () => {
            updateSafetyAnswers(
              input
            );
          }
        );
      }
    );

    methodsNextButton?.addEventListener(
      "click",
      () => {
        if (
          !Array.isArray(
            state.answers.methods_used
          ) ||
          state.answers.methods_used
            .length === 0
        ) {
          return;
        }

        showQuestion(
          "zones",
          16
        );

        updateZonesAnswer();
      }
    );

    zonesBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "methods_used",
          8
        );
      }
    );

    zonesNextButton?.addEventListener(
      "click",
      () => {
        if (
          !Array.isArray(
            state.answers.zones
          ) ||
          state.answers.zones.length === 0 ||
          !state.answers.zone_primary
        ) {
          return;
        }

        syncPrimaryZoneLabel();

        if (hasMultipleMethods()) {
          syncMethodForZoneOptions();

          showQuestion(
            "method_for_primary_zone",
            24
          );

          return;
        }

        clearMethodForZoneAnswer();

        showQuestion(
          "frequency",
          32
        );
      }
    );

    methodForZoneBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "zones",
          16
        );

        updateZonesAnswer();
      }
    );

    methodForZoneNextButton?.addEventListener(
      "click",
      () => {
        if (
          !hasMultipleMethods() ||
          !state.answers
            .method_for_primary_zone
        ) {
          return;
        }

        syncPrimaryZoneLabel();

        showQuestion(
          "frequency",
          32
        );
      }
    );

    frequencyBackButton?.addEventListener(
      "click",
      () => {
        if (hasMultipleMethods()) {
          syncMethodForZoneOptions();
          syncPrimaryZoneLabel();

          showQuestion(
            "method_for_primary_zone",
            24
          );

          return;
        }

        showQuestion(
          "zones",
          16
        );

        updateZonesAnswer();
      }
    );

    frequencyNextButton?.addEventListener(
      "click",
      () => {
        if (!state.answers.frequency) {
          return;
        }

        syncPrimaryZoneLabel();
        syncRegrowthQuestion();

        showQuestion(
          "regrowth_timing",
          40
        );
      }
    );

    regrowthBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "frequency",
          32
        );
      }
    );

    regrowthNextButton?.addEventListener(
      "click",
      () => {
        if (
          !state.answers.regrowth_timing
        ) {
          return;
        }

        syncPrimaryZoneLabel();

        showQuestion(
          "density_declared",
          48
        );
      }
    );

    densityBackButton?.addEventListener(
      "click",
      () => {
        syncRegrowthQuestion();

        showQuestion(
          "regrowth_timing",
          40
        );
      }
    );

    densityNextButton?.addEventListener(
      "click",
      () => {
        if (
          !state.answers.density_declared
        ) {
          return;
        }

        syncPrimaryZoneLabel();

        showQuestion(
          "thickness_declared",
          56
        );
      }
    );

    thicknessBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "density_declared",
          48
        );
      }
    );

    thicknessNextButton?.addEventListener(
      "click",
      () => {
        if (
          !state.answers.thickness_declared
        ) {
          return;
        }

        syncPrimaryZoneLabel();

        showQuestion(
          "post_epilation_signs",
          64
        );
      }
    );

    postEpilationSignsBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "thickness_declared",
          56
        );
      }
    );

    postEpilationSignsNextButton?.addEventListener(
      "click",
      () => {
        if (
          !Array.isArray(
            state.answers.post_epilation_signs
          ) ||
          state.answers.post_epilation_signs
            .length === 0
        ) {
          return;
        }

        if (shouldAskReactionDuration()) {
          showQuestion(
            "reaction_duration",
            72
          );

          return;
        }

        clearReactionDurationAnswer();
        syncPrimaryZoneLabel();

        showQuestion(
          "ingrown_frequency",
          80
        );
      }
    );

    reactionDurationBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "post_epilation_signs",
          64
        );
      }
    );

    reactionDurationNextButton?.addEventListener(
      "click",
      () => {
        if (
          !state.answers.reaction_duration
        ) {
          return;
        }

        syncPrimaryZoneLabel();

        showQuestion(
          "ingrown_frequency",
          80
        );
      }
    );

    ingrownFrequencyBackButton?.addEventListener(
      "click",
      () => {
        if (shouldAskReactionDuration()) {
          showQuestion(
            "reaction_duration",
            72
          );

          return;
        }

        showQuestion(
          "post_epilation_signs",
          64
        );
      }
    );
    ingrownFrequencyNextButton?.addEventListener(
      "click",
      () => {
        if (
          !state.answers.ingrown_frequency
        ) {
          return;
        }

        if (shouldAskIngrownManifestations()) {
          syncPrimaryZoneLabel();

          showQuestion(
            "ingrown_manifestations",
            84
          );

          return;
        }

        clearIngrownManifestationsAnswer();
        syncPrimaryZoneLabel();

        showQuestion(
          "comfort_perception",
          88
        );
      }
    );

    ingrownManifestationsBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "ingrown_frequency",
          80
        );
      }
    );

    ingrownManifestationsNextButton?.addEventListener(
      "click",
      () => {
        if (
          !Array.isArray(
            state.answers.ingrown_manifestations
          ) ||
          state.answers.ingrown_manifestations
            .length === 0
        ) {
          return;
        }

        syncPrimaryZoneLabel();

        showQuestion(
          "comfort_perception",
          88
        );
      }
    );

    comfortPerceptionBackButton?.addEventListener(
      "click",
      () => {
        if (shouldAskIngrownManifestations()) {
          showQuestion(
            "ingrown_manifestations",
            84
          );

          return;
        }

        showQuestion(
          "ingrown_frequency",
          80
        );
      }
    );

    comfortPerceptionNextButton?.addEventListener(
      "click",
      () => {
        if (
          !state.answers.comfort_perception
        ) {
          return;
        }

        syncPrimaryZoneLabel();

        showQuestion(
          "routine",
          92
        );
      }
    );

    routineBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "comfort_perception",
          88
        );
      }
    );

    routineNextButton?.addEventListener(
      "click",
      () => {
        if (
          !Array.isArray(
            state.answers.routine
          ) ||
          state.answers.routine.length === 0
        ) {
          return;
        }

        if (shouldAskExfoliationTiming()) {
          showQuestion(
            "exfoliation_timing",
            95
          );

          return;
        }

        clearExfoliationTimingAnswer();

        showQuestion(
          "priority",
          97
        );
      }
    );

    exfoliationTimingBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "routine",
          92
        );
      }
    );

    exfoliationTimingNextButton?.addEventListener(
      "click",
      () => {
        if (
          !state.answers.exfoliation_timing
        ) {
          return;
        }

        showQuestion(
          "priority",
          97
        );
      }
    );

    priorityBackButton?.addEventListener(
      "click",
      () => {
        if (shouldAskExfoliationTiming()) {
          showQuestion(
            "exfoliation_timing",
            95
          );

          return;
        }

        showQuestion(
          "routine",
          92
        );
      }
    );
    priorityNextButton?.addEventListener(
      "click",
      async () => {
        if (!state.answers.priority) {
          return;
        }

        if (shouldAskSafetyGate()) {
          syncPrimaryZoneLabel();

          showQuestion(
            "safety_answers",
            99
          );

          return;
        }

        clearSafetyAnswers();

        startGenerationSequence();
      }
    );

    safetyAnswersBackButton?.addEventListener(
      "click",
      () => {
        showQuestion(
          "priority",
          97
        );
      }
    );

    safetyAnswersNextButton?.addEventListener(
      "click",
      () => {
        if (
          !Array.isArray(
            state.answers.safety_answers
          ) ||
          state.answers.safety_answers.length === 0
        ) {
          return;
        }

        startGenerationSequence();
      }
    );

    showScreen("intro");

    questions.forEach((question) => {
      question.hidden =
        question.dataset.bilanQuestion !==
        "methods_used";
    });

    setProgress(0);

    updateMethodsAnswer();
    updateZonesAnswer();
    syncMethodForZoneOptions();
    updateMethodForZoneAnswer();
    updateFrequencyAnswer();
    syncRegrowthQuestion();
    updateRegrowthAnswer();
    updateDensityAnswer();
    updateThicknessAnswer();
    updatePostEpilationSignsAnswer();
    updateReactionDurationAnswer();
    updateIngrownFrequencyAnswer();
    updateIngrownManifestationsAnswer();
    updateComfortPerceptionAnswer();
    updateRoutineAnswer();
    updateExfoliationTimingAnswer();
    updatePriorityAnswer();
    syncSafetyGateState();
    updateSafetyAnswersNextState();

    firstNameInput?.addEventListener(
      "input",
      syncFirstName
    );

    firstNameInput?.addEventListener(
      "keydown",
      (event) => {
        if (
          event.key !== "Enter"
        ) {
          return;
        }

        event.preventDefault();

        syncFirstName();

        if (
          state.lead.first_name
        ) {
          showConsentScreen();
        }
      }
    );

    firstNameNextButton?.addEventListener(
      "click",
      showConsentScreen
    );

    identityBackButton?.addEventListener(
      "click",
      () => {
        setExperienceMode(false);

        showScreen("intro");

        setProgress(0);
      }
    );

    dataConsentInput?.addEventListener(
      "change",
      syncDataConsent
    );

    consentBackButton?.addEventListener(
      "click",
      showIdentityScreen
    );

    consentNextButton?.addEventListener(
      "click",
      beginQuestionnaire
    );

    generationNextButton?.addEventListener(
      "click",
      () => {
        if (
          generationNextButton.disabled
        ) {
          return;
        }

        showEmailGate();
      }
    );

    emailInput?.addEventListener(
      "input",
      syncEmailGate
    );

    marketingConsentInput?.addEventListener(
      "change",
      syncEmailGate
    );

    emailBackButton?.addEventListener(
      "click",
      returnFromEmailGate
    );

    emailSubmitButton?.addEventListener(
      "click",
      async () => {
        syncEmailGate();

        if (
          !isValidEmail(
            state.lead.email
          )
        ) {
          return;
        }

        await submitBilan(
          emailSubmitButton
        );
      }
    );

    /*
     * Une modification de formulaire
     * déclenche une sauvegarde après
     * les handlers spécifiques de l'input.
     */

    bilan.addEventListener(
      "change",
      () => {
        saveBilanDraft();
      }
    );

    recoveryResumeButton?.addEventListener(
      "click",
      () => {
        const draft =
          readBilanDraft();

        if (!draft) {
          clearBilanDraft();
          handleStart();
          return;
        }

        trackBilanEvent(
          "recovery_resumed",
          {
            recovery_type:
              "draft"
          }
        );

        resumeDraft(
          draft
        );
      }
    );

    recoveryRestartButton?.addEventListener(
      "click",
      () => {
        trackBilanEvent(
          "restart",
          {
            restart_from:
              "draft_recovery"
          }
        );

        clearBilanDraft();

        window.location.reload();
      }
    );


    resultRecoveryResumeButton?.addEventListener(
      "click",
      async () => {
        const session =
          readBilanResultSession();

        if (!session) {
          const fallbackDraft =
            readBilanDraft();

          if (fallbackDraft) {
            showRecoveryScreen(
              fallbackDraft
            );

            return;
          }

          clearBilanResultSession();
          handleStart();
          return;
        }

        trackBilanEvent(
          "recovery_resumed",
          {
            recovery_type:
              "result_session"
          }
        );

        completedResultId =
          session.result_id;

        completedAccessToken =
          session.access_token;

        completedResult =
          null;

        state.lead.first_name =
          typeof session.first_name ===
            "string"
            ? session.first_name
            : "";

        firstNameDisplays.forEach(
          (element) => {
            element.textContent =
              state.lead.first_name;
          }
        );

        if (
          resultRecoveryError
        ) {
          resultRecoveryError.hidden =
            true;

          resultRecoveryError.textContent =
            "";
        }

        resultRecoveryResumeButton.disabled =
          true;

        resultRecoveryResumeButton.innerHTML =
          'Chargement de votre Bilan <span aria-hidden="true">…</span>';

        try {
          const result =
            await loadBilanResult();

          /*
           * Le résultat récupéré doit être
           * rendu exactement dans le même
           * contexte que le résultat normal.
           */

          setExperienceMode(
            false
          );

          renderResultHero(
            result
          );

          trackBilanEvent(
            "result_viewed",
            {
              load_mode:
                "recovered"
            }
          );
        } catch (error) {
          trackBilanEvent(
            "error",
            {
              error_stage:
                "result_recovery",

              online:
                navigator.onLine
            }
          );

          console.error(
            "BERYT Bilan — erreur de reprise du résultat",
            error
          );

          if (
            resultRecoveryError
          ) {
            resultRecoveryError.textContent =
              navigator.onLine
                ? "Nous n’avons pas pu recharger votre Bilan pour le moment. Vous pouvez réessayer sans recommencer le questionnaire."
                : "Votre connexion semble interrompue. Reconnectez-vous puis réessayez : votre Bilan reste disponible dans cette session.";

            resultRecoveryError.hidden =
              false;
          }

          resultRecoveryResumeButton.innerHTML =
            'Réessayer <span aria-hidden="true">→</span>';

          resultRecoveryResumeButton.disabled =
            false;
        }
      }
    );

    resultRecoveryRestartButton?.addEventListener(
      "click",
      () => {
        trackBilanEvent(
          "restart",
          {
            restart_from:
              "result_session_recovery"
          }
        );

        clearBilanResultSession();
        clearBilanDraft();

        window.location.reload();
      }
    );


    startButton?.addEventListener(
      "click",
      handleStart
    );

    syncFirstName();
    syncDataConsent();

    const searchParams =
      new URLSearchParams(
        window.location.search
      );

    const magicLinkToken =
      String(
        searchParams.get(
          "bilan_token"
        ) ?? ""
      ).trim();

    const shouldDownloadMagicPdf =
      searchParams.get(
        "download"
      ) === "1";

    const shouldAutoStart =
      searchParams.get(
        "start"
      ) === "1";

    const detectedResultSession =
      !window.Shopify?.designMode
        ? readBilanResultSession()
        : null;

    const detectedDraft =
      !window.Shopify?.designMode
        ? readBilanDraft()
        : null;

    /*
     * Un lien venant de l'e-mail
     * est prioritaire sur une ancienne
     * session ou un ancien brouillon
     * éventuellement présents
     * dans le navigateur.
     */

    if (
      magicLinkToken &&
      !window.Shopify?.designMode
    ) {
      void recoverBilanFromMagicLink(
        magicLinkToken,
        shouldDownloadMagicPdf
      );
    } else if (
      detectedResultSession
    ) {
      completedResultId =
        detectedResultSession
          .result_id;

      completedAccessToken =
        detectedResultSession
          .access_token;

      setExperienceMode(
        true
      );

      showScreen(
        "result-recovery"
      );

      trackBilanEvent(
        "recovery_shown",
        {
          recovery_type:
            "result_session"
        }
      );

      setProgress(
        100
      );

      scrollToBilanTop();
    } else if (
      detectedDraft
    ) {
      showRecoveryScreen(
        detectedDraft
      );
    } else if (
      shouldAutoStart &&
      !window.Shopify?.designMode
    ) {
      handleStart();
    }

    window.__BERYT_BILAN_STATE__ =
      state;
  };

  const initAllBerytBilans = () => {
    document
      .querySelectorAll(
        "[data-beryt-bilan]"
      )
      .forEach(initBerytBilan);
  };

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initAllBerytBilans
    );
  } else {
    initAllBerytBilans();
  }

  document.addEventListener(
    "shopify:section:load",
    initAllBerytBilans
  );
})();